// NewProductScreen.tsx — the Add New Product (V6) single-page flow.
// Design: docs/design/design_handoff_add_new_product/ (README + newproduct-v6.jsx).
// Rendered by App.tsx when currentView === "new-product".
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useReducer, useRef, useState } from "react";
import { Tooltip, CircularProgress, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import { setCurrentProject } from "@/state/project/projectSlice";
import {
    initialNewProductState,
    newProductReducer,
    isLocked,
    canCreate,
    hasRequiredTargetUser,
    collectStagedIds,
    contextMeshAnalysisRevisionKey,
    refreshContextMeshAnalysisRevisionKey,
    railSteps,
    applyNewProductDraftPatch,
    buildNewProductDraftSnapshot,
    buildPersistedNewProductDraftState,
    buildProductMeshSubmission,
    buildProductCreationContext,
    NFR_FIELDS,
    nfrValidationErrors,
    workProductAnswerValidationErrors,
    hasWorkProductAnswerValidationErrors,
    hydrateNewProductState,
    hydrateStateFromRefreshCore,
    isNewProductDraftDirty,
    migratePersistedDraft,
    productCreationDraftOffering,
    type NewProductState,
    type NewProductDraftPatch,
    type NewProductDraftSnapshot,
    type ProductKind,
    type NfrKey,
    type SourceConnection,
    type SourceId,
    type PrimaryFormFactor,
    StepId
} from "./state";
import { completeProductCreationDraft, discardProductCreationDraft, getProductCreationDraft, saveProductCreationDraft } from "./draftApi";
import { inferField, type InferableField } from "./inferApi";
import { FieldQualityNote } from "./FieldQualityNote";
import { useFieldQuality } from "./useFieldQuality";
import { attachStagedArtifacts, cleanupStagedArtifacts, type StagedAttachSources } from "@/api/stagedArtifacts";
import { startProductBuild } from "../products/buildApi";
import { requestProductContextMeshAnalysis } from "../products/api";
import fetchWithAuth from "@/api/secureFetch";
import { NPIcon, NPButton, OptionalBadge, RequiredBadge, NP_DASH } from "./atoms";
import { ExpandingField } from "./ExpandingField";
import { ListEditorCard } from "./ListEditorCard";
import { ProgressRail } from "./ProgressRail";
import { MeshCard } from "./MeshCard";
import { MeshInspector } from "./MeshInspector";
import { useDraftContextMeshAnalysis } from "./useDraftContextMeshAnalysis";
import { isExactReadyDraftContextMesh, scheduleDraftContextMeshAnalysis } from "./draftContextMeshApi";
import {
    discardRefreshContextMeshPreview,
    getRefreshContextMeshAnalysis,
    saveRefreshContextMeshPreview,
    scheduleRefreshContextMeshAnalysis
} from "./refreshContextMeshApi";
import { useMeshStrengthData } from "../meshStrength/useMeshStrengthData";
import { KindPills } from "./KindPills";
import { IntegrationsBar } from "./IntegrationsBar";
import { ConnectSourceModal } from "./connect/ConnectSourceModal";
import type { ConnectSourceId } from "./connect/sourceConfigs";
import { FilesModal } from "./FilesModal";
import { RefreshFilesModal } from "./RefreshFilesModal";
import { ConfirmDialog } from "./ConfirmDialog";
import { RefreshBanner } from "./RefreshBanner";
import { loadRefreshSource, buildNewAnswers, type RefreshSource } from "../productRefresh/loader";
import { runRefreshScan } from "../productRefresh/scan";
import { commitMinorEvolution, commitRefinementOnly, writeCoreUpdates } from "../productRefresh/commit";
import { markFilesChanged, markIntegrationsChanged } from "../productRefresh/files";
import { startDrasticChange, runDrasticBuild, type DrasticStartResult } from "../productRefresh/drastic";
import type { RefreshCoreValues, RefreshVerdict } from "../productRefresh/types";
import {
    ScanProgressOverlay,
    NoChangesDialog,
    RefinementOnlyDialog,
    MinorEvolutionDialog,
    DrasticChangeDialog,
    BuildProgressOverlay
} from "../productRefresh/RefreshDialogs";
import "./tokens.css";
import { NavigationBackButton } from "../NavigationBackButton";
import {
    clearProductCreationOperationId,
    getOrCreateProductCreationOperationId,
    launchProductCreation,
    ProductCreationOperationApiError,
    type ProductCreationOperation
} from "./productCreationOperationsApi";

const INFER_TIP = "Infer this from what you've entered so far.";
const INFER_DISABLED_TIP = "Fill in the problem field first — inference drafts from it.";
const AGENT_URL = import.meta.env.VITE_AGENT_URL || "http://localhost:8000";
const refreshFilesConnection = (files: RefreshSource["files"]): SourceConnection => ({
    connected: files.length > 0,
    selection: files.map(file => ({ id: String(file.fileId), name: file.fileName })),
    syncedCount: files.length
});
const FORM_FACTOR_OPTIONS: Array<{ value: PrimaryFormFactor; label: string }> = [
    { value: "web", label: "Web" },
    { value: "mobile", label: "Mobile" },
    { value: "api", label: "API" }
];
const FORM_FACTOR_PILL_SX = {
    border: "none",
    borderRadius: "999px !important",
    padding: "5px 14px",
    fontSize: "11.5px",
    fontWeight: 600,
    lineHeight: 1.5,
    textTransform: "none",
    whiteSpace: "nowrap",
    fontFamily: "inherit",
    background: "var(--bg-input)",
    color: "var(--fg3)",
    "&:hover": { background: "var(--bg-input)" },
    "&.Mui-selected": {
        background: "var(--np-brand)",
        color: "#fff",
        "&:hover": { background: "var(--np-brand-hover)" }
    }
} as const;

export function formatLocalDraftSaveTime(timestamp: string): string {
    const hasTimeZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(timestamp);
    const absoluteTimestamp = hasTimeZone ? timestamp : `${timestamp}Z`;
    return new Date(absoluteTimestamp).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export function productCreationErrorMessage(result: unknown, status: number): string {
    const fallback = `Product creation failed (${status})`;
    if (!result || typeof result !== "object") return fallback;
    const detail = (result as { detail?: unknown }).detail;
    if (typeof detail === "string" && detail.trim()) return detail;
    if (detail && typeof detail === "object") {
        const message = (detail as { message?: unknown }).message;
        if (typeof message === "string" && message.trim()) return message;
    }
    return fallback;
}

function InferButton({ onInfer, disabled, busy }: { onInfer: () => void; disabled: boolean; busy: boolean }) {
    return (
        <Tooltip title={disabled && !busy ? INFER_DISABLED_TIP : INFER_TIP} arrow>
            <span>
                <NPButton tone="soft" small disabled={disabled || busy} onClick={onInfer}>
                    {busy ? <CircularProgress size={12} sx={{ color: "var(--np-brand)" }} /> : <NPIcon name="sparkle" size={12} color="var(--np-brand)" />}
                    Infer
                </NPButton>
            </span>
        </Tooltip>
    );
}

interface NewProductScreenProps {
    onCancel: () => void;
    /** Opens Product Hub when creation is accepted. */
    onCreated: (projectId?: number, creationOperation?: ProductCreationOperation) => void;
    /** Launch the durable CodeNexus workflow instead of running creation in this screen. */
    asyncProductCreationEnabled?: boolean;
    /** Clerk user id used to isolate browser idempotency state across accounts. */
    idempotencyUserScope?: string | null;
    draftRef?: React.MutableRefObject<NewProductDraftSnapshot | null>;
    /** Resume this saved draft; omit to start a fresh one. */
    draftId?: number | null;
    /** Pre-answer for the New-vs-Existing (Product Type) gate — set when the FDE
     *  navigated here with the user's intent already known. Applied only if the
     *  loaded draft has no kind yet; never overrides a resumed draft's choice. */
    initialKind?: ProductKind | null;
    /** Initial values supplied by an in-app creation entry point. Applied once
     *  to a fresh draft after it loads, and never over a resumed draft. */
    initialDraftPatch?: NewProductDraftPatch | null;
    /** Refresh modes edit an existing Product or standalone Context Mesh in place. */
    mode?: "create" | "refresh" | "refresh-context-mesh";
    /** Required when mode === "refresh": the product being refreshed. */
    refreshProductId?: number;
    /** Refresh mode completion callback — wired by Task 7 (scan/commit). Unused this task. */
    onRefreshComplete?: (outcome: { kind: "updated" } | { kind: "rebuilt"; newProjectId: number }) => void;
    /** Standalone Context Mesh refresh title and commit adapter. */
    refreshContextTitle?: string;
    onRefreshContextMesh?: (state: NewProductState) => Promise<void>;
    onRefreshContextMeshComplete?: () => void;
    /** Files routed here programmatically (FDE chat drops → attach_file_to_new_product).
     *  Opens the Files modal and feeds them through the normal upload+staging path. */
    intakeFiles?: File[];
    onIntakeConsumed?: () => void;
    /** Shown in refresh mode when opened from the product's Context Mesh. */
    onBackToContextMesh?: () => void;
    /** Reuses this complete form and its draft analysis without entering any Product creation path. */
    creationExperience?: "product" | "standalone-context-mesh";
    /** Creates the standalone Context Mesh, including any source attachment required by the caller. */
    onCreateStandaloneContextMesh?: (
        state: NewProductState,
        persisted: StandaloneContextMeshDraftIdentity
    ) => Promise<string>;
    /** Called after standalone creation and temporary analysis-draft cleanup both settle. */
    onStandaloneContextMeshCreated?: (contextMeshId: string) => void;
    /** Publishes the durable draft id so the standalone route can be bookmarked/restored. */
    onDraftIdentityChange?: (draftId: number) => void;
    /** Reports whether this form contains user-entered content, independent of autosave state. */
    onDirtyChange?: (dirty: boolean) => void;
}

export interface StandaloneContextMeshDraftIdentity {
    draftId: number;
    draftVersion: number;
}

export interface NewProductFormHandle {
    applyDraftPatch: (patch: NewProductDraftPatch) => { ok: true; draft: NewProductDraftSnapshot } | { ok: false; error: string };
}

export const NewProductScreen = React.forwardRef<NewProductFormHandle, NewProductScreenProps>(function NewProductScreen(
    {
        onCancel,
        onCreated,
        asyncProductCreationEnabled = false,
        idempotencyUserScope = null,
        draftRef,
        draftId,
        initialKind,
        initialDraftPatch,
        mode = "create",
        refreshProductId,
        onRefreshComplete,
        refreshContextTitle,
        onRefreshContextMesh,
        onRefreshContextMeshComplete,
        intakeFiles,
        onIntakeConsumed,
        onBackToContextMesh,
        creationExperience = "product",
        onCreateStandaloneContextMesh,
        onStandaloneContextMeshCreated,
        onDraftIdentityChange,
        onDirtyChange
    },
    formRef
) {
    const isProductRefresh = mode === "refresh";
    const isContextMeshRefresh = mode === "refresh-context-mesh";
    const isRefresh = isProductRefresh || isContextMeshRefresh;
    const isStandaloneContextMesh = creationExperience === "standalone-context-mesh";
    const draftOffering = isStandaloneContextMesh ? "context_mesh" : "product_studio";
    const draftPurpose = isContextMeshRefresh ? "context_mesh_refresh" as const : undefined;
    const pendingExitActionRef = useRef<(() => void) | null>(null);
    const [state, d] = useReducer(newProductReducer, initialNewProductState);
    // Refresh mode has its own loading gate (refreshLoaded, below) and never
    // resumes a create-mode draft, so draftLoaded starts satisfied.
    const [draftLoaded, setDraftLoaded] = useState(isProductRefresh);
    const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
    const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
    const [savedFingerprint, setSavedFingerprint] = useState("");
    const [persistedDraftIdentity, setPersistedDraftIdentity] = useState<{ draftId: number | null; version: number | null }>({
        draftId: draftId ?? null,
        version: null
    });
    const meshSnapshotRef = useRef<NewProductDraftSnapshot["contextMeshAnalysis"]>({
        status: "unavailable",
        score: null,
        tier: null,
        analyzedDraftVersion: null
    });
    const [openSource, setOpenSource] = useState<SourceId | null>(null);
    const [pendingConfirm, setPendingConfirm] = useState<"startOver" | "saveDraft" | "cancel" | null>(null);
    const dispatch = useAppDispatch();
    const token = useAppSelector(s => s.clientToken.token);
    const userId = useAppSelector(s => s.userProfiles.userProfile?.userid);
    const dirty = isNewProductDraftDirty(state);
    // Refresh mode bypasses the kind/scope lock rule (behavior 4) — the
    // reducer always hydrates with kind: "new" in refresh mode, so
    // isLocked(state) would already read false, but this makes the bypass
    // explicit rather than incidental.
    const locked = !isRefresh && isLocked(state);
    const interactionLocked = locked || state.isCreating;
    const lockStyle: React.CSSProperties = interactionLocked
        ? { opacity: 0.45, pointerEvents: "none", filter: "grayscale(0.3)" }
        : {};
    const [inferringFields, setInferringFields] = useState<ReadonlySet<InferableField>>(() => new Set());
    const inferRequestsRef = useRef<Map<InferableField, AbortController>>(new Map());
    const [nfrOpen, setNfrOpen] = useState(false);
    const [existingContextOpen, setExistingContextOpen] = useState(true);
    const [featureContextOpen, setFeatureContextOpen] = useState(true);
    const answerLengthErrors = useMemo(() => workProductAnswerValidationErrors(state), [state]);
    const hasAnswerLengthErrors = hasWorkProductAnswerValidationErrors(answerLengthErrors);
    const nfrErrors = useMemo(
        () => ({ ...answerLengthErrors.nfr, ...nfrValidationErrors(state) }),
        [answerLengthErrors.nfr, state.nfr]
    );
    const persistedState = useMemo(
        () => buildPersistedNewProductDraftState(state, draftOffering, draftPurpose),
        [draftOffering, draftPurpose, state]
    );
    const currentFingerprint = useMemo(() => JSON.stringify(persistedState), [persistedState]);
    const analysisRevisionKey = useMemo(() => contextMeshAnalysisRevisionKey(state), [state]);
    const refreshAnalysisRevisionKey = useMemo(() => refreshContextMeshAnalysisRevisionKey(state), [state]);
    const hasUnsavedChanges = draftLoaded && dirty && currentFingerprint !== savedFingerprint;
    // One-tracker rule (Jira XOR Azure DevOps): once one is staged/connected,
    // block staging the other up front rather than failing at build time.
    const trackerBlocked = useMemo(() => {
        const active = (c: SourceConnection) => Boolean(c.connected || c.staged?.processJobId || c.selection?.length);
        const jira = active(state.connections.jira);
        const azure = active(state.connections.azure);
        const blocked: Partial<Record<SourceId, string>> = {};
        // If BOTH are somehow active (legacy dirty draft), block neither so the
        // user can open each modal and disconnect one.
        if (jira && !azure) blocked.azure = "Jira is already connected — products sync with one tracker";
        if (azure && !jira) blocked.jira = "Azure DevOps is already connected — products sync with one tracker";
        return blocked;
    }, [state.connections.jira, state.connections.azure]);
    const latestStateRef = useRef(state);
    // App-level route callbacks are commonly declared inline and therefore
    // receive a new identity whenever the shell rerenders. Keep the latest
    // callback available without making the draft persistence callbacks
    // unstable: otherwise the Context Mesh analysis effect continually resets
    // its four-second debounce and a resumed draft can remain "Analyzing"
    // forever without ever scheduling a score run.
    const onDraftIdentityChangeRef = useRef(onDraftIdentityChange);
    onDraftIdentityChangeRef.current = onDraftIdentityChange;
    const initialDraftPatchAppliedRef = useRef(false);
    const saveQueueRef = useRef<Promise<unknown>>(Promise.resolve());
    const savedRevisionRef = useRef<{ draftId: number; draftVersion: number; revisionKey: string } | null>(null);
    const repositorySelectionSaveInFlightRef = useRef(false);
    const refreshPreviewSaveQueueRef = useRef<Promise<unknown>>(Promise.resolve());
    const refreshPreviewSavedRevisionRef = useRef<{ draftId: number; draftVersion: number; revisionKey: string } | null>(null);
    const refreshPreviewDraftIdRef = useRef<number | null>(null);
    const [refreshPreviewDraftId, setRefreshPreviewDraftId] = useState<number | null>(null);
    const [refreshPreviewHasCompletedAnalysis, setRefreshPreviewHasCompletedAnalysis] = useState(false);
    const [createPreflightBlocked, setCreatePreflightBlocked] = useState(false);
    const preflightObservedPendingRef = useRef(false);
    const isSubmittingRef = useRef(false);
    const retriedDraftVersionRef = useRef<string | null>(null);
    // The persisted draft's workproduct id: seeded when resuming, captured from
    // the first successful save otherwise. Reads happen inside the save queue so
    // a create followed by a queued save cannot double-create.
    const draftIdRef = useRef<number | null>(draftId ?? null);

    // Refresh mode (KAN-1464): the loaded product snapshot, retained for
    // diffing (dirty-vs-sourceRef CTA enablement) and for "Revert edits".
    // Task 7 wires the actual scan/commit; this task only loads + hydrates.
    const [sourceRef, setSourceRef] = useState<RefreshSource | null>(null);
    const [refreshLoaded, setRefreshLoaded] = useState(!isProductRefresh);
    const [refreshLoadError, setRefreshLoadError] = useState<string | null>(null);
    const [refreshFilesChanged, setRefreshFilesChanged] = useState(false);

    // Refresh mode (KAN-1464 Task 7): staged scan -> verdict -> commit flow.
    // `refreshPhase` drives which overlay/dialog is on screen; `scanResult`
    // retains the classified verdict + raw comparison_result for the active
    // dialog and the eventual commit call. Nothing here writes to the
    // product — see handleRefreshScan/handleRefreshConfirm below for the
    // staged-write invariant (no mutation before a verdict dialog confirm).
    type RefreshPhase = "idle" | "scanning" | { verdict: RefreshVerdict } | "committing" | "buildingDrastic";
    const [refreshPhase, setRefreshPhase] = useState<RefreshPhase>("idle");
    const [scanResult, setScanResult] = useState<unknown>(null);
    const [buildProgress, setBuildProgress] = useState<{ phase: string; message?: string }>({ phase: "" });

    // Normalize list values: trim and filter empties (follows isNewProductDraftDirty convention).
    const normList = (xs: string[]) => xs.map(v => v.trim()).filter(v => v !== "");

    // Refresh mode CTA (behavior 5) + exit-guard dirtiness: true when any
    // field/users/team differs from the loaded sourceRef. Declared above the
    // beforeunload effect (below) because in refresh mode `savedFingerprint`
    // stays "" and isNewProductDraftDirty(state) is structurally true right
    // after hydrate — using the create-mode `hasUnsavedChanges` fingerprint
    // logic for refresh mode would arm beforeunload/requestExit even with
    // zero edits. refreshDirtyVsSource compares against the actual loaded
    // source instead.
    const refreshCoreDirtyVsSource =
        sourceRef != null &&
        (state.fields.name !== sourceRef.core.name ||
            state.fields.problem !== sourceRef.core.problem ||
            state.fields.solution !== sourceRef.core.solution ||
            NFR_FIELDS.some(field => state.nfr[field.key] !== (sourceRef.core.nfr?.[field.key] ?? "")) ||
            JSON.stringify(normList(state.users)) !== JSON.stringify(normList(sourceRef.users)) ||
            JSON.stringify(normList(state.team)) !== JSON.stringify(normList(sourceRef.team)));
    const refreshIntegrationDirtyVsSource =
        sourceRef != null &&
        (Object.keys(state.connections) as SourceId[])
            .filter(source => source !== "files")
            .some(source => {
                const current = state.connections[source];
                const baseline = sourceRef.connections?.[source] ?? { connected: false };
                return current.connected !== baseline.connected || Boolean(current.staged?.processJobId || current.staged?.repoIds?.length);
            });
    const refreshDiscoverDirtyVsSource =
        sourceRef != null &&
        (state.fields.problem !== sourceRef.core.problem ||
            state.fields.solution !== sourceRef.core.solution ||
            JSON.stringify(normList(state.users)) !== JSON.stringify(normList(sourceRef.users)));
    const refreshScoreDirtyVsSource = refreshDiscoverDirtyVsSource || refreshFilesChanged || refreshIntegrationDirtyVsSource;
    const refreshDirtyVsSource = refreshCoreDirtyVsSource || refreshFilesChanged || refreshIntegrationDirtyVsSource;
    const contextRefreshBaseline = useMemo(
        () => initialDraftPatch ? applyNewProductDraftPatch(initialNewProductState, initialDraftPatch) : null,
        [initialDraftPatch]
    );
    const contextRefreshDirtyVsSource = isContextMeshRefresh && contextRefreshBaseline != null && (
        state.fields.name !== contextRefreshBaseline.fields.name ||
        state.fields.problem !== contextRefreshBaseline.fields.problem ||
        state.fields.solution !== contextRefreshBaseline.fields.solution ||
        state.existingProduct.name.trim() !== contextRefreshBaseline.existingProduct.name.trim() ||
        state.existingProduct.purpose.trim() !== contextRefreshBaseline.existingProduct.purpose.trim() ||
        state.existingProduct.capabilities.trim() !== contextRefreshBaseline.existingProduct.capabilities.trim() ||
        JSON.stringify(normList(state.existingProduct.users)) !== JSON.stringify(normList(contextRefreshBaseline.existingProduct.users)) ||
        NFR_FIELDS.some(field => state.nfr[field.key] !== contextRefreshBaseline.nfr[field.key]) ||
        JSON.stringify(normList(state.users)) !== JSON.stringify(normList(contextRefreshBaseline.users)) ||
        JSON.stringify(normList(state.team)) !== JSON.stringify(normList(contextRefreshBaseline.team))
    );

    useEffect(() => {
        latestStateRef.current = state;
    }, [state]);

    useEffect(() => {
        onDirtyChange?.(dirty);
    }, [dirty, onDirtyChange]);

    useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

    // Product/Feature/Story hierarchy actions can open this existing form with
    // the relevant product context already filled in. Keep resumed drafts as
    // the source of truth and apply entry-point context only to a fresh form.
    useEffect(() => {
        if (isProductRefresh || !draftLoaded || draftId != null || initialDraftPatch == null || initialDraftPatchAppliedRef.current) return;
        initialDraftPatchAppliedRef.current = true;
        const nextState = applyNewProductDraftPatch(latestStateRef.current, initialDraftPatch);
        latestStateRef.current = nextState;
        d({ type: "applyDraftPatch", patch: initialDraftPatch });
    }, [draftId, draftLoaded, initialDraftPatch, isProductRefresh]);

    // FDE navigation pre-answer: apply initialKind once the draft has loaded,
    // but only while the gate is unanswered — a resumed draft's kind wins.
    useEffect(() => {
        if (isRefresh || !draftLoaded || initialKind == null) return;
        if (latestStateRef.current.kind !== null) return;
        d({ type: "setKind", kind: initialKind });
    }, [isRefresh, draftLoaded, initialKind]);

    // Chat-dropped files routed to this screen: surface the Files modal so the
    // pending intake queue starts feeding the upload flow (FilesModal pulls the
    // head of the queue itself).
    useEffect(() => {
        if (isRefresh || !draftLoaded || !intakeFiles?.length) return;
        setOpenSource("files");
    }, [isRefresh, draftLoaded, intakeFiles]);

    useImperativeHandle(
        formRef,
        () => ({
            applyDraftPatch: patch => {
                if (!draftLoaded) {
                    return { ok: false, error: "The Product Creation form is still loading." };
                }
                const previousState = latestStateRef.current;
                const nextState = applyNewProductDraftPatch(previousState, patch);
                latestStateRef.current = nextState;
                d({ type: "applyDraftPatch", patch });
                const nextFingerprint = JSON.stringify(buildPersistedNewProductDraftState(nextState, draftOffering, draftPurpose));
                const analysisInputsChanged = contextMeshAnalysisRevisionKey(nextState) !== contextMeshAnalysisRevisionKey(previousState);
                const nextBaselineReady =
                    nextState.fields.name.trim().length >= 2 &&
                    nextState.fields.problem.trim().length >= 15 &&
                    nextState.fields.solution.trim().length >= 15 &&
                    hasRequiredTargetUser(nextState.users) &&
                    (nextState.kind !== "existing" ||
                        (nextState.existingProduct.name.trim().length >= 2 &&
                            nextState.existingProduct.purpose.trim().length >= 15 &&
                            nextState.existingProduct.capabilities.trim() !== "" &&
                            nextState.existingProduct.users.some(user => user.trim() !== "")));
                const contextMeshAnalysis = analysisInputsChanged
                    ? {
                          ...meshSnapshotRef.current,
                          status: meshSnapshotRef.current.score === null
                              ? nextBaselineReady ? "analyzing" as const : "unavailable" as const
                              : "stale" as const
                      }
                    : meshSnapshotRef.current;
                return {
                    ok: true,
                    draft: {
                        ...buildNewProductDraftSnapshot(nextState, contextMeshAnalysis),
                        hasUnsavedChanges: isNewProductDraftDirty(nextState) && nextFingerprint !== savedFingerprint
                    }
                };
            }
        }),
        [draftLoaded, draftOffering, draftPurpose, savedFingerprint]
    );

    useEffect(() => {
        if (isProductRefresh) return; // Product refresh has its own private preview draft.
        if (!token) return;
        if (draftId == null) {
            setSavedFingerprint(JSON.stringify(buildPersistedNewProductDraftState(initialNewProductState, draftOffering, draftPurpose)));
            setDraftLoaded(true);
            return;
        }
        let cancelled = false;
        setDraftLoaded(false);
        getProductCreationDraft(token, draftId)
            .then(draft => {
                if (cancelled) return;
                if (draft?.draft_state) {
                    if (productCreationDraftOffering(draft.draft_state) !== draftOffering) {
                        draftIdRef.current = null;
                        savedRevisionRef.current = null;
                        setPersistedDraftIdentity({ draftId: null, version: null });
                        setSavedFingerprint(JSON.stringify(buildPersistedNewProductDraftState(initialNewProductState, draftOffering, draftPurpose)));
                        toast.error(
                            isStandaloneContextMesh
                                ? "That draft belongs to Product Studio and cannot be opened in Context Mesh Hub."
                                : "That draft belongs to Context Mesh Hub and cannot be opened in Product Hub."
                        );
                        onCancel();
                        return;
                    }
                    const migrated = migratePersistedDraft(draft.draft_state);
                    const restored = hydrateNewProductState(migrated);
                    d({ type: "hydrate", state: restored });
                    setSavedFingerprint(JSON.stringify(buildPersistedNewProductDraftState(restored, draftOffering, draftPurpose)));
                    savedRevisionRef.current = {
                        draftId: draft.draft_id,
                        draftVersion: draft.version,
                        revisionKey: contextMeshAnalysisRevisionKey(restored)
                    };
                    setPersistedDraftIdentity({ draftId: draft.draft_id, version: draft.version });
                    setLastSavedAt(draft.updated_at);
                    setSaveStatus("saved");
                } else {
                    draftIdRef.current = null;
                    savedRevisionRef.current = null;
                    setPersistedDraftIdentity({ draftId: null, version: null });
                    setSavedFingerprint(JSON.stringify(buildPersistedNewProductDraftState(initialNewProductState, draftOffering, draftPurpose)));
                    toast.warning("That draft no longer exists — starting a new one.");
                }
            })
            .catch(error => {
                if (cancelled) return;
                setSaveStatus("error");
                toast.error(error instanceof Error ? error.message : `Failed to load ${isStandaloneContextMesh ? "Context Mesh" : "product"} draft`);
            })
            .finally(() => {
                if (!cancelled) setDraftLoaded(true);
            });
        return () => {
            cancelled = true;
        };
    }, [draftId, draftOffering, draftPurpose, isProductRefresh, token]);

    // Refresh mode (KAN-1464 behavior 1): load the current product snapshot
    // and hydrate the reducer. `draftLoaded` is forced true above (via
    // refreshLoaded's initial state) but the screen's own loading gate below
    // uses refreshLoaded so the spinner shows until the fetch resolves.
    useEffect(() => {
        if (!isProductRefresh) return;
        if (!token || refreshProductId == null || userId == null) return;
        let cancelled = false;
        setRefreshLoaded(false);
        setRefreshLoadError(null);
        loadRefreshSource(token, refreshProductId, userId)
            .then(source => {
                if (cancelled) return;
                setSourceRef(source);
                d({
                    type: "hydrate",
                    state: hydrateStateFromRefreshCore(source.core, source.users, source.team, {
                        ...(source.connections ?? {}),
                        files: refreshFilesConnection(source.files)
                    })
                });
            })
            .catch(error => {
                if (cancelled) return;
                const message = error instanceof Error ? error.message : "Failed to load product";
                setRefreshLoadError(message);
                toast.error(message);
            })
            .finally(() => {
                if (!cancelled) setRefreshLoaded(true);
            });
        return () => {
            cancelled = true;
        };
    }, [isProductRefresh, token, refreshProductId, userId]);

    const saveDraftSnapshot = useCallback(
        async (nextState: NewProductState) => {
            if (!token) throw new Error("Your session hasn't finished loading");
            const payload = buildPersistedNewProductDraftState(nextState, draftOffering, draftPurpose);
            const fingerprint = JSON.stringify(payload);
            const saved = await saveProductCreationDraft(token, payload, draftIdRef.current);
            draftIdRef.current = saved.draft_id;
            savedRevisionRef.current = {
                draftId: saved.draft_id,
                draftVersion: saved.version,
                revisionKey: contextMeshAnalysisRevisionKey(nextState)
            };
            setPersistedDraftIdentity({ draftId: saved.draft_id, version: saved.version });
            onDraftIdentityChangeRef.current?.(saved.draft_id);
            setSavedFingerprint(fingerprint);
            setLastSavedAt(saved.updated_at);
            setSaveStatus("saved");
            return saved;
        },
        [draftOffering, draftPurpose, token]
    );

    const persistDraft = useCallback(
        (nextState = latestStateRef.current) => {
            setSaveStatus("saving");
            const request = saveQueueRef.current
                .catch(() => undefined)
                .then(() => saveDraftSnapshot(nextState))
                .catch(error => {
                    setSaveStatus("error");
                    throw error;
                });
            saveQueueRef.current = request;
            return request;
        },
        [saveDraftSnapshot]
    );

    const ensureDraftRevisionPersisted = useCallback(
        (
            revisionKey: string,
            forceSave = false,
            staleDraftVersion?: number
        ): Promise<{ draftId: number; draftVersion: number }> => {
            const savedAtInvocation = savedRevisionRef.current;
            const request = saveQueueRef.current
                .catch(() => undefined)
                .then(async () => {
                    // Recheck after prior queue entries complete. Two analysis
                    // callers can arrive together, but only the first should
                    // persist this semantic revision. A forced stale retry is
                    // coalesced only when another caller already refreshed the
                    // same revision after this request was queued.
                    const saved = savedRevisionRef.current;
                    const newerSameRevision = saved?.revisionKey === revisionKey && (
                        savedAtInvocation == null ||
                        saved.draftId !== savedAtInvocation.draftId ||
                        saved.draftVersion !== savedAtInvocation.draftVersion
                    );
                    const alreadyAdvancedPastConflict = saved?.revisionKey === revisionKey &&
                        staleDraftVersion != null && saved.draftVersion !== staleDraftVersion;
                    if (
                        saved &&
                        saved.revisionKey === revisionKey &&
                        (!forceSave || newerSameRevision || alreadyAdvancedPastConflict)
                    ) {
                        return { draftId: saved.draftId, draftVersion: saved.draftVersion };
                    }
                    const snapshot = latestStateRef.current;
                    if (contextMeshAnalysisRevisionKey(snapshot) !== revisionKey) {
                        throw new Error("The draft changed before analysis could begin.");
                    }
                    setSaveStatus("saving");
                    try {
                        const persisted = await saveDraftSnapshot(snapshot);
                        return { draftId: persisted.draft_id, draftVersion: persisted.version };
                    } catch (error) {
                        setSaveStatus("error");
                        throw error;
                    }
                });
            saveQueueRef.current = request;
            return request;
        },
        [saveDraftSnapshot]
    );

    const saveRefreshPreviewSnapshot = useCallback(
        async (nextState: NewProductState) => {
            if (!token || refreshProductId == null) {
                throw new Error("Your session hasn't finished loading");
            }
            const saved = await saveRefreshContextMeshPreview(
                token,
                refreshProductId,
                buildPersistedNewProductDraftState(nextState, draftOffering, draftPurpose)
            );
            refreshPreviewDraftIdRef.current = saved.draftId;
            refreshPreviewSavedRevisionRef.current = {
                draftId: saved.draftId,
                draftVersion: saved.draftVersion,
                revisionKey: refreshContextMeshAnalysisRevisionKey(nextState)
            };
            setRefreshPreviewDraftId(saved.draftId);
            return saved;
        },
        [draftOffering, draftPurpose, refreshProductId, token]
    );

    const ensureRefreshPreviewPersisted = useCallback(
        (
            revisionKey: string,
            forceSave = false,
            staleDraftVersion?: number
        ): Promise<{ draftId: number; draftVersion: number }> => {
            const savedAtInvocation = refreshPreviewSavedRevisionRef.current;
            const request = refreshPreviewSaveQueueRef.current
                .catch(() => undefined)
                .then(async () => {
                    // The field-quality and mesh-analysis debounces can expire
                    // together. Rechecking here, rather than before queuing,
                    // makes the second request reuse the first saved revision.
                    const saved = refreshPreviewSavedRevisionRef.current;
                    const newerSameRevision = saved?.revisionKey === revisionKey && (
                        savedAtInvocation == null ||
                        saved.draftId !== savedAtInvocation.draftId ||
                        saved.draftVersion !== savedAtInvocation.draftVersion
                    );
                    const alreadyAdvancedPastConflict = saved?.revisionKey === revisionKey &&
                        staleDraftVersion != null && saved.draftVersion !== staleDraftVersion;
                    if (
                        saved &&
                        saved.revisionKey === revisionKey &&
                        (!forceSave || newerSameRevision || alreadyAdvancedPastConflict)
                    ) {
                        return { draftId: saved.draftId, draftVersion: saved.draftVersion };
                    }
                    const snapshot = latestStateRef.current;
                    if (refreshContextMeshAnalysisRevisionKey(snapshot) !== revisionKey) {
                        throw new Error("The refresh preview changed before analysis could begin.");
                    }
                    const persisted = await saveRefreshPreviewSnapshot(snapshot);
                    return { draftId: persisted.draftId, draftVersion: persisted.draftVersion };
                });
            refreshPreviewSaveQueueRef.current = request;
            return request;
        },
        [saveRefreshPreviewSnapshot]
    );

    const ensureQualityDraftPersisted = useCallback(
        (
            field: "problem" | "solution" | "personas",
            value: string,
            forceSave = false,
            staleDraftVersion?: number
        ) => {
            const snapshot = latestStateRef.current;
            const currentValue = field === "personas"
                ? snapshot.users.map(item => item.trim()).filter(Boolean).join(", ")
                : snapshot.fields[field].trim();
            if (currentValue !== value) {
                return Promise.reject(new Error("The field changed before evaluation could begin."));
            }
            return isProductRefresh
                ? ensureRefreshPreviewPersisted(refreshContextMeshAnalysisRevisionKey(snapshot), forceSave, staleDraftVersion)
                : ensureDraftRevisionPersisted(contextMeshAnalysisRevisionKey(snapshot), forceSave, staleDraftVersion);
        },
        [ensureDraftRevisionPersisted, ensureRefreshPreviewPersisted, isProductRefresh]
    );
    // Discover evaluations always belong to an exact persisted draft revision.
    // Refresh-preview rows remain staged until the Product update commits.
    const {
        quality,
        evaluate: evaluateQuality,
        evaluateAndWait: evaluateQualityAndWait,
        schedule: scheduleQuality,
        invalidate: invalidateQuality
    } = useFieldQuality(token, {
        enabled: !locked,
        ensurePersisted: ensureQualityDraftPersisted
    });
    const evaluatePersonasQuality = useCallback(() => {
        const value = latestStateRef.current.users.map(item => item.trim()).filter(Boolean).join(", ");
        void evaluateQuality("personas", value);
    }, [evaluateQuality]);
    const updatePersonasAndEvaluate = useCallback(
        (
            action:
                | { type: "removeListItem"; list: "users"; index: number }
                | { type: "clearList"; list: "users" }
        ) => {
            const nextState = newProductReducer(latestStateRef.current, action);
            latestStateRef.current = nextState;
            d(action);
            const value = nextState.users.map(item => item.trim()).filter(Boolean).join(", ");
            evaluateQuality("personas", value);
        },
        [evaluateQuality]
    );

    const canonicalPersonasValue = useMemo(
        () => state.users.map(item => item.trim()).filter(Boolean).join(", "),
        [state.users]
    );
    useEffect(() => {
        if (locked) return;
        if (!isRefresh || !sourceRef || state.fields.problem.trim() !== sourceRef.core.problem.trim()) {
            scheduleQuality("problem", state.fields.problem);
        }
    }, [isRefresh, locked, scheduleQuality, sourceRef, state.fields.problem]);
    useEffect(() => {
        if (locked) return;
        if (!isRefresh || !sourceRef || state.fields.solution.trim() !== sourceRef.core.solution.trim()) {
            scheduleQuality("solution", state.fields.solution);
        }
    }, [isRefresh, locked, scheduleQuality, sourceRef, state.fields.solution]);
    useEffect(() => {
        if (locked) return;
        if (!isRefresh || !sourceRef || canonicalPersonasValue !== normList(sourceRef.users).join(", ")) {
            scheduleQuality("personas", canonicalPersonasValue);
        }
    }, [canonicalPersonasValue, isRefresh, locked, scheduleQuality, sourceRef]);

    const ensureRefreshAnalysisPersisted = useCallback(
        async (revisionKey: string, forceSave = false): Promise<{ draftId: number; draftVersion: number }> => {
            const persisted = await ensureRefreshPreviewPersisted(revisionKey, forceSave);
            const snapshot = latestStateRef.current;
            if (refreshContextMeshAnalysisRevisionKey(snapshot) !== revisionKey) {
                throw new Error("The refresh preview changed before analysis could begin.");
            }
            if (!sourceRef) return persisted;

            // A blur still provides immediate field feedback, but Context Mesh
            // analysis must not wait forever when a user removes a Persona or
            // moves directly to another control. After the normal four-second
            // analysis debounce, evaluate every changed Discover input against
            // this exact persisted preview revision before requesting scores.
            const changedFields: Array<["problem" | "solution" | "personas", string]> = [];
            const problem = snapshot.fields.problem.trim();
            const solution = snapshot.fields.solution.trim();
            const personas = snapshot.users.map(item => item.trim()).filter(Boolean).join(", ");
            if (problem !== sourceRef.core.problem.trim()) changedFields.push(["problem", problem]);
            if (solution !== sourceRef.core.solution.trim()) changedFields.push(["solution", solution]);
            if (personas !== normList(sourceRef.users).join(", ") && personas) {
                changedFields.push(["personas", personas]);
            }

            const results = await Promise.all(
                changedFields.map(([field, value]) => evaluateQualityAndWait(field, value))
            );
            if (results.some(result => !result)) {
                throw new Error("Discover context evaluation failed — try again.");
            }
            return persisted;
        },
        [ensureRefreshPreviewPersisted, evaluateQualityAndWait, sourceRef]
    );
    const cancelInference = useCallback((field?: InferableField) => {
        if (field) {
            const controller = inferRequestsRef.current.get(field);
            if (!controller) return;
            controller.abort();
            inferRequestsRef.current.delete(field);
            setInferringFields(current => {
                if (!current.has(field)) return current;
                const next = new Set(current);
                next.delete(field);
                return next;
            });
            return;
        }

        for (const controller of inferRequestsRef.current.values()) {
            controller.abort();
        }
        inferRequestsRef.current.clear();
        setInferringFields(new Set());
    }, []);
    useEffect(() => () => cancelInference(), [cancelInference]);
    const runInfer = useCallback(
        async (field: InferableField) => {
            if (!token) {
                toast.error("Your session hasn't finished loading — try again in a moment.");
                return;
            }
            inferRequestsRef.current.get(field)?.abort();
            const controller = new AbortController();
            inferRequestsRef.current.set(field, controller);
            setInferringFields(current => new Set(current).add(field));
            try {
                const result = await inferField(field, state, token, { signal: controller.signal });
                if (controller.signal.aborted) return;
                if (field === "solution" && result.value !== undefined) {
                    const action = { type: "setField", field: "solution", value: result.value } as const;
                    latestStateRef.current = newProductReducer(latestStateRef.current, action);
                    d(action);
                    evaluateQuality("solution", result.value);
                } else if (field === "users" && result.values !== undefined) {
                    const action = { type: "appendListItems", list: "users", values: result.values } as const;
                    const nextState = newProductReducer(latestStateRef.current, action);
                    latestStateRef.current = nextState;
                    d(action);
                    evaluateQuality("personas", nextState.users.map(item => item.trim()).filter(Boolean).join(", "));
                } else if (field.startsWith("nfr_") && result.value !== undefined) {
                    const key = field.slice(4) as NfrKey;
                    if (NFR_FIELDS.some(definition => definition.key === key)) {
                        d({ type: "setNfrField", field: key, value: result.value });
                    }
                }
            } catch (error) {
                if (!controller.signal.aborted) {
                    toast.error(error instanceof Error ? error.message : "Inference failed — try again.");
                }
            } finally {
                if (inferRequestsRef.current.get(field) === controller) {
                    inferRequestsRef.current.delete(field);
                    setInferringFields(current => {
                        const next = new Set(current);
                        next.delete(field);
                        return next;
                    });
                }
            }
        },
        [evaluateQuality, state, token]
    );
    const canInfer = state.fields.problem.trim() !== "";

    const getRefreshPreviewAnalysis = useCallback(
        (analysisToken: string, draftId: number) => {
            if (refreshProductId == null) return Promise.reject(new Error("A product is required for refresh analysis."));
            return getRefreshContextMeshAnalysis(analysisToken, refreshProductId, draftId);
        },
        [refreshProductId]
    );

    const scheduleRefreshPreviewAnalysis = useCallback(
        (analysisToken: string, draftId: number, draftVersion: number) => {
            if (refreshProductId == null) return Promise.reject(new Error("A product is required for refresh analysis."));
            return scheduleRefreshContextMeshAnalysis(analysisToken, refreshProductId, draftId, draftVersion);
        },
        [refreshProductId]
    );

    const discardRefreshPreview = useCallback(async (): Promise<void> => {
        await refreshPreviewSaveQueueRef.current.catch(() => undefined);
        if (token && refreshProductId != null && refreshPreviewDraftIdRef.current != null) {
            await discardRefreshContextMeshPreview(token, refreshProductId);
        }
        refreshPreviewDraftIdRef.current = null;
        refreshPreviewSavedRevisionRef.current = null;
        setRefreshPreviewDraftId(null);
        setRefreshPreviewHasCompletedAnalysis(false);
        invalidateQuality("problem");
        invalidateQuality("solution");
        invalidateQuality("personas");
    }, [invalidateQuality, refreshProductId, token]);

    const existingProductBaselineReady =
        state.kind !== "existing" ||
        (state.existingProduct.name.trim().length >= 2 &&
            state.existingProduct.purpose.trim().length >= 15 &&
            state.existingProduct.capabilities.trim() !== "" &&
            state.existingProduct.users.some(user => user.trim() !== ""));
    const canonicalBaselineReady =
        state.fields.name.trim().length >= 2 &&
        state.fields.problem.trim().length >= 15 &&
        state.fields.solution.trim().length >= 15 &&
        hasRequiredTargetUser(state.users) &&
        existingProductBaselineReady;
    const draftMesh = useDraftContextMeshAnalysis({
        enabled: !isProductRefresh && draftLoaded,
        token,
        draftId: persistedDraftIdentity.draftId,
        revisionKey: analysisRevisionKey,
        baselineReady: canonicalBaselineReady,
        ensurePersisted: ensureDraftRevisionPersisted
    });
    const refreshPreviewMesh = useDraftContextMeshAnalysis({
        enabled: isProductRefresh && refreshLoaded && refreshScoreDirtyVsSource,
        token,
        draftId: refreshPreviewDraftId,
        revisionKey: refreshAnalysisRevisionKey,
        baselineReady: canonicalBaselineReady,
        ensurePersisted: ensureRefreshAnalysisPersisted,
        getAnalysis: getRefreshPreviewAnalysis,
        scheduleAnalysis: scheduleRefreshPreviewAnalysis
    });
    const productMesh = useMeshStrengthData(isProductRefresh ? refreshProductId ?? null : null);
    const completeProductMesh = productMesh.analysis?.categoryScores
        ? { ...productMesh.analysis, categoryScores: productMesh.analysis.categoryScores }
        : null;
    const currentRefreshPreview = refreshPreviewSavedRevisionRef.current;
    const refreshPreviewScoreIsCurrent =
        currentRefreshPreview !== null &&
        currentRefreshPreview.revisionKey === refreshAnalysisRevisionKey &&
        refreshPreviewMesh.analysis !== null &&
        refreshPreviewMesh.status === "ready" &&
        !refreshPreviewMesh.isStale &&
        refreshPreviewMesh.analyzedDraftVersion === currentRefreshPreview.draftVersion;
    const meshAnalysis = isProductRefresh
        ? refreshScoreDirtyVsSource && refreshPreviewHasCompletedAnalysis
            ? refreshPreviewMesh.analysis ?? completeProductMesh
            : completeProductMesh
        : draftMesh.analysis;
    const meshStatus = isProductRefresh
        ? refreshScoreDirtyVsSource
            ? canonicalBaselineReady ? refreshPreviewMesh.status : "baseline_incomplete"
            : productMesh.status
        : canonicalBaselineReady ? draftMesh.status : "baseline_incomplete";
    const meshStale = isProductRefresh
        ? refreshScoreDirtyVsSource
            ? meshAnalysis !== null && (refreshPreviewMesh.analysis === null || refreshPreviewMesh.isStale || refreshPreviewMesh.status !== "ready")
            : productMesh.status === "stale"
        : draftMesh.isStale || (!canonicalBaselineReady && draftMesh.analysis !== null);
    const meshError = isProductRefresh
        ? refreshScoreDirtyVsSource
            ? canonicalBaselineReady ? refreshPreviewMesh.error?.message ?? null : null
            : productMesh.error?.message ?? null
        : canonicalBaselineReady ? draftMesh.error?.message ?? null : null;
    const meshUpdating = isProductRefresh
        ? refreshScoreDirtyVsSource
            ? canonicalBaselineReady && (
                refreshPreviewMesh.status === "debouncing" ||
                refreshPreviewMesh.status === "pending" ||
                refreshPreviewMesh.status === "sources_pending"
            )
            : productMesh.loading ||
              productMesh.status === "analyzing" ||
              productMesh.analysis?.semanticAnalysis.status === "pending" ||
              productMesh.analysis?.categoryAnalysis?.status === "pending"
        : canonicalBaselineReady &&
          (draftMesh.status === "debouncing" || draftMesh.status === "pending" || draftMesh.status === "sources_pending");
    const retryMesh = isProductRefresh
        ? refreshScoreDirtyVsSource ? refreshPreviewMesh.retry : productMesh.retry
        : draftMesh.retry;
    const createMeshIsExactCurrent =
        !isProductRefresh &&
        canonicalBaselineReady &&
        draftMesh.analysis !== null &&
        draftMesh.status === "ready" &&
        !draftMesh.isStale &&
        draftMesh.analyzedDraftVersion !== null &&
        persistedDraftIdentity.version !== null &&
        draftMesh.analyzedDraftVersion === persistedDraftIdentity.version &&
        savedRevisionRef.current?.revisionKey === analysisRevisionKey;
    const createIsReady =
        canCreate(state) &&
        createMeshIsExactCurrent &&
        !createPreflightBlocked &&
        !hasUnsavedChanges &&
        saveStatus !== "saving" &&
        saveStatus !== "error";

    // A metadata-only autosave can advance the draft row while leaving the
    // semantic revision key unchanged. Refresh the exact-version result so
    // creation never promotes an older draft revision.
    useEffect(() => {
        if (
            isProductRefresh ||
            !canonicalBaselineReady ||
            persistedDraftIdentity.version == null ||
            draftMesh.analyzedDraftVersion == null ||
            draftMesh.analyzedDraftVersion === persistedDraftIdentity.version
        ) return;
        const retryKey = `${persistedDraftIdentity.draftId ?? "draft"}:${persistedDraftIdentity.version}`;
        if (retriedDraftVersionRef.current === retryKey) return;
        retriedDraftVersionRef.current = retryKey;
        draftMesh.retry();
    }, [
        canonicalBaselineReady,
        draftMesh.analyzedDraftVersion,
        draftMesh.retry,
        isProductRefresh,
        persistedDraftIdentity.draftId,
        persistedDraftIdentity.version
    ]);

    useEffect(() => {
        if (!createPreflightBlocked) return;
        if (!createMeshIsExactCurrent) {
            preflightObservedPendingRef.current = true;
            return;
        }
        if (preflightObservedPendingRef.current) {
            preflightObservedPendingRef.current = false;
            setCreatePreflightBlocked(false);
        }
    }, [createMeshIsExactCurrent, createPreflightBlocked]);
    const fdeMeshSnapshot: NewProductDraftSnapshot["contextMeshAnalysis"] = {
        status: meshAnalysis
            ? meshUpdating || meshStale ? "stale" : "ready"
            : meshError ? "failed" : meshUpdating ? "analyzing" : "unavailable",
        score: meshAnalysis?.score ?? null,
        tier: meshAnalysis?.tier ?? null,
        analyzedDraftVersion: isProductRefresh
            ? refreshPreviewHasCompletedAnalysis ? refreshPreviewMesh.analyzedDraftVersion : null
            : draftMesh.analyzedDraftVersion
    };
    meshSnapshotRef.current = fdeMeshSnapshot;

    useEffect(() => {
        if (
            isProductRefresh &&
            refreshScoreDirtyVsSource &&
            refreshPreviewMesh.analysis !== null &&
            refreshPreviewMesh.status === "ready" &&
            !refreshPreviewMesh.isStale
        ) {
            setRefreshPreviewHasCompletedAnalysis(true);
        }
    }, [
        isProductRefresh,
        refreshScoreDirtyVsSource,
        refreshPreviewMesh.analysis,
        refreshPreviewMesh.isStale,
        refreshPreviewMesh.status
    ]);

    useEffect(() => {
        if (!isProductRefresh || !refreshLoaded || refreshScoreDirtyVsSource || refreshPreviewDraftIdRef.current == null) return;
        void discardRefreshPreview().catch(error => {
            console.error("Failed to discard reverted Context Mesh preview", error);
        });
    }, [discardRefreshPreview, isProductRefresh, refreshLoaded, refreshScoreDirtyVsSource]);

    useEffect(() => {
        // Refresh mode never autosaves a draft (behavior 6) — nothing is
        // written until the user confirms a scan verdict (Task 7).
        if (isProductRefresh || repositorySelectionSaveInFlightRef.current || !draftLoaded || !dirty || !hasUnsavedChanges || !token) return;
        const timer = window.setTimeout(() => {
            void persistDraft(state).catch(() => undefined);
        }, 750);
        return () => window.clearTimeout(timer);
    }, [dirty, draftLoaded, hasUnsavedChanges, isProductRefresh, persistDraft, state, token]);

    useEffect(() => {
        if (!draftRef) return;
        draftRef.current = { ...buildNewProductDraftSnapshot(state, fdeMeshSnapshot), hasUnsavedChanges };
    }, [draftRef, fdeMeshSnapshot.analyzedDraftVersion, fdeMeshSnapshot.score, fdeMeshSnapshot.status, fdeMeshSnapshot.tier, hasUnsavedChanges, state]);

    useEffect(() => {
        // Refresh mode: savedFingerprint never advances (no autosave) and
        // isNewProductDraftDirty(state) reads true right after hydrate, so
        // hasUnsavedChanges would arm this prompt even with zero edits. Use
        // refreshDirtyVsSource (diffed against the loaded sourceRef) instead.
        const armed = isProductRefresh
            ? refreshDirtyVsSource
            : isContextMeshRefresh ? contextRefreshDirtyVsSource : hasUnsavedChanges;
        if (!armed) return;
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [contextRefreshDirtyVsSource, hasUnsavedChanges, isContextMeshRefresh, isProductRefresh, refreshDirtyVsSource]);

    useEffect(
        () => () => {
            if (draftRef) draftRef.current = null;
        },
        [draftRef]
    );

    const steps = railSteps(state).filter(step => !isContextMeshRefresh || step.id !== "integrations");

    const sectionRefs = useRef<Partial<Record<StepId, HTMLDivElement | null>>>({});
    const setRef = (id: StepId) => (el: HTMLDivElement | null) => {
        sectionRefs.current[id] = el;
    };
    const scrollTo = (id: StepId) => {
        if (isRefresh && id === "nfr") setNfrOpen(true);
        if (id === "existing") setExistingContextOpen(true);
        if (id === "feature" || id === "users") setFeatureContextOpen(true);
        const target = sectionRefs.current[id];
        target?.scrollIntoView({ behavior: "smooth", block: "center" });
    };

    const hasStagedSource = (connection: SourceConnection) =>
        Boolean(connection.staged?.processJobId || connection.staged?.repoIds?.length || connection.staged?.fileIds?.length);

    const buildStagedAttachSources = (stagedOnly = false): StagedAttachSources =>
        Object.fromEntries(
            (Object.entries(state.connections) as [SourceId, SourceConnection][])
                .filter(([, conn]) => conn.connected && (!stagedOnly || hasStagedSource(conn)))
                .map(([source, conn]) => {
                    if (source === "files") {
                        return [source, { fileIds: conn.staged?.fileIds ?? [] }];
                    }
                    if (conn.staged?.repoIds) {
                        return [source, { repoIds: conn.staged.repoIds, selection: conn.selection }];
                    }
                    return [source, { processJobId: conn.staged?.processJobId, selection: conn.selection }];
                })
        );

    const handleSourceComplete = (source: SourceId, connection: SourceConnection) => {
        const current = latestStateRef.current.connections[source];
        if (isRefresh && hasStagedSource(current) && token) {
            const staged = current.staged;
            cleanupStagedArtifacts(
                {
                    processJobIds: staged?.processJobId ? [staged.processJobId] : [],
                    repoIds: staged?.repoIds ?? []
                },
                token
            ).catch(error => console.error("Failed to clean up replaced staged artifacts", error));
        }
        d({
            type: "setConnection",
            source,
            connection: isRefresh ? { ...connection, existingLinkIds: current.existingLinkIds } : connection
        });
    };

    const handleRepositorySelectionStaged = async (
        source: SourceId,
        connection: SourceConnection
    ): Promise<{ draftId: number; draftVersion: number }> => {
        // The durable import endpoint accepts only repository ids that are
        // already part of this exact saved draft revision. Update the local
        // form and serialize all selected ids before scheduling any work.
        const nextState = newProductReducer(latestStateRef.current, {
            type: "setConnection",
            source,
            connection
        });
        repositorySelectionSaveInFlightRef.current = true;
        latestStateRef.current = nextState;
        d({ type: "setConnection", source, connection });
        try {
            const saved = await persistDraft(nextState);
            return { draftId: saved.draft_id, draftVersion: saved.version };
        } finally {
            repositorySelectionSaveInFlightRef.current = false;
        }
    };

    const handleSourceDisconnect = (source: SourceId) => {
        const conn = latestStateRef.current.connections[source];
        const staged = conn.staged;
        if (staged && (staged.processJobId || staged.repoIds?.length || staged.fileIds?.length) && token) {
            cleanupStagedArtifacts(
                {
                    processJobIds: staged.processJobId ? [staged.processJobId] : [],
                    repoIds: staged.repoIds ?? [],
                    fileIds: staged.fileIds ?? []
                },
                token
            ).catch(error => console.error("Failed to clean up staged artifacts", error));
        }
        if (isRefresh) {
            d({
                type: "setConnection",
                source,
                connection: {
                    connected: false,
                    existingLinkIds: conn.existingLinkIds
                }
            });
        } else {
            d({ type: "disconnectSource", source });
        }
    };

    const handleSourceAborted = (staged: { processJobId?: string; repoIds?: number[] }) => {
        if (!token) return;
        if (!staged.processJobId && !staged.repoIds?.length) return;
        cleanupStagedArtifacts(
            {
                processJobIds: staged.processJobId ? [staged.processJobId] : [],
                repoIds: staged.repoIds ?? []
            },
            token
        ).catch(error => console.error("Failed to clean up staged artifacts", error));
    };

    const handleFilesUploaded = (fileId?: number, fileName?: string) => {
        if (fileId == null) return;
        const existing = latestStateRef.current.connections.files;
        if (existing.staged?.fileIds?.includes(fileId)) return;
        const fileIds = [...(existing.staged?.fileIds ?? []), fileId];
        const selection = [...(existing.selection ?? []), { id: String(fileId), name: fileName ?? `File #${fileId}` }];
        d({
            type: "setConnection",
            source: "files",
            connection: { ...existing, connected: true, selection, staged: { ...existing.staged, fileIds } }
        });
    };

    const handleFileRemoved = (fileId: number) => {
        const existing = latestStateRef.current.connections.files;
        const fileIds = (existing.staged?.fileIds ?? []).filter(id => id !== fileId);
        const selection = (existing.selection ?? []).filter(item => item.id !== String(fileId));
        d({
            type: "setConnection",
            source: "files",
            connection: {
                ...existing,
                connected: fileIds.length > 0,
                selection,
                staged: { ...existing.staged, fileIds }
            }
        });
    };

    const handleCreate = async () => {
        if (isSubmittingRef.current) return;
        if (!createIsReady) return;
        if (!token) {
            toast.error("Your session hasn't finished loading — try again in a moment.");
            return;
        }
        isSubmittingRef.current = true;
        d({ type: "setCreating", creating: true });
        try {
            let persisted: { draftId: number; draftVersion: number };
            try {
                // The button is gated on a completed autosave, but resolve the
                // exact saved identity again at click time so UI-bridge and
                // rapid pointer interactions cannot bypass the revision lock.
                persisted = await ensureDraftRevisionPersisted(analysisRevisionKey);
            } catch (error) {
                toast.error(error instanceof Error ? error.message : `Save the ${isStandaloneContextMesh ? "Context Mesh" : "product"} draft before creating it`);
                d({ type: "setCreating", creating: false });
                return;
            }
            if (isContextMeshRefresh) {
                if (!onRefreshContextMesh) {
                    toast.error("Context Mesh refresh is unavailable.");
                    d({ type: "setCreating", creating: false });
                    return;
                }
                try {
                    const preflight = await scheduleDraftContextMeshAnalysis(token, persisted.draftId, persisted.draftVersion);
                    if (!isExactReadyDraftContextMesh(preflight, persisted.draftId, persisted.draftVersion)) {
                        preflightObservedPendingRef.current = false;
                        setCreatePreflightBlocked(true);
                        draftMesh.retry();
                        toast.info("Context analysis is still finishing. Apply will be available when it is ready.");
                        d({ type: "setCreating", creating: false });
                        return;
                    }
                    await onRefreshContextMesh(state);
                    await discardProductCreationDraft(token, persisted.draftId, { cleanupStaged: true });
                    draftIdRef.current = null;
                    savedRevisionRef.current = null;
                    toast.success(`${state.fields.name.trim()} refreshed`);
                    onRefreshContextMeshComplete?.();
                } catch (error) {
                    toast.error(error instanceof Error ? error.message : "Failed to refresh Context Mesh");
                    d({ type: "setCreating", creating: false });
                }
                return;
            }
            if (isStandaloneContextMesh) {
                if (!onCreateStandaloneContextMesh) {
                    toast.error("Standalone Context Mesh creation is unavailable.");
                    d({ type: "setCreating", creating: false });
                    return;
                }
                try {
                    const preflight = await scheduleDraftContextMeshAnalysis(token, persisted.draftId, persisted.draftVersion);
                    if (!isExactReadyDraftContextMesh(preflight, persisted.draftId, persisted.draftVersion)) {
                        preflightObservedPendingRef.current = false;
                        setCreatePreflightBlocked(true);
                        draftMesh.retry();
                        toast.info("Context analysis is still finishing. Create will be available when it is ready.");
                        d({ type: "setCreating", creating: false });
                        return;
                    }

                    const contextMeshId = await onCreateStandaloneContextMesh(state, persisted);
                    if (typeof contextMeshId !== "string" || contextMeshId.trim() === "") {
                        throw new Error("Context Mesh creation returned an invalid identifier.");
                    }

                    // The completed draft is retained server-side as a hidden,
                    // immutable scoring subject. Workspace refreshes rescore
                    // that snapshot with the same vector-v2 anchors used while
                    // creating it; it is Submitted, so neither Hub renders it
                    // as an unfinished draft.
                    draftIdRef.current = null;
                    savedRevisionRef.current = null;
                    setPersistedDraftIdentity({ draftId: null, version: null });
                    d({ type: "reset" });
                    setSavedFingerprint(JSON.stringify(buildPersistedNewProductDraftState(initialNewProductState, draftOffering, draftPurpose)));
                    setLastSavedAt(null);
                    setSaveStatus("idle");
                    if (draftRef) draftRef.current = null;
                    toast.success(`${state.fields.name.trim()} created`);
                    onStandaloneContextMeshCreated?.(contextMeshId);
                } catch (error) {
                    toast.error(error instanceof Error ? error.message : "Failed to create Context Mesh");
                    d({ type: "setCreating", creating: false });
                }
                return;
            }
            if (asyncProductCreationEnabled) {
                if (!idempotencyUserScope) {
                    toast.error("Your account session is not ready. Try Create again in a moment.");
                    d({ type: "setCreating", creating: false });
                    return;
                }
                const submission = buildProductMeshSubmission(state);
                const operationId = getOrCreateProductCreationOperationId(idempotencyUserScope, persisted.draftId, persisted.draftVersion);
                try {
                    const operation = await launchProductCreation(token, operationId, {
                        working_name: submission.working_name,
                        form_factor: submission.form_factor,
                        draft_id: persisted.draftId,
                        draft_version: persisted.draftVersion,
                        mesh: submission.mesh
                    });
                    toast.success(`${state.fields.name.trim()} creation started`);
                    if (draftRef) draftRef.current = null;
                    onCreated(undefined, operation);
                } catch (error) {
                    if (error instanceof ProductCreationOperationApiError) {
                        if (error.code === "CONTEXT_MESH_NOT_READY" || error.code === "DRAFT_VERSION_STALE") {
                            preflightObservedPendingRef.current = false;
                            setCreatePreflightBlocked(true);
                            draftMesh.retry();
                            toast.info(error.message);
                            d({ type: "setCreating", creating: false });
                            return;
                        }
                        if (error.code === "DRAFT_ALREADY_LAUNCHED") {
                            clearProductCreationOperationId(idempotencyUserScope, persisted.draftId, persisted.draftVersion);
                            toast.info("Product creation is already running. Opening Product Hub.");
                            if (draftRef) draftRef.current = null;
                            onCreated();
                            return;
                        }
                        if (error.code === "PRODUCT_CREATION_LAUNCH_FAILED") {
                            toast.error(`${error.message} Open the Product card to retry.`);
                            if (draftRef) draftRef.current = null;
                            onCreated();
                            return;
                        }
                        if (
                            error.code === "IDEMPOTENCY_KEY_UNAVAILABLE"
                            || error.code === "IDEMPOTENCY_KEY_REUSED"
                            || error.code === "PRODUCT_CREATION_LAUNCH_CANCELED"
                        ) {
                            clearProductCreationOperationId(idempotencyUserScope, persisted.draftId, persisted.draftVersion);
                            toast.error(`${error.message} Select Create again to start a new request.`);
                            d({ type: "setCreating", creating: false });
                            return;
                        }
                    }
                    toast.error(error instanceof Error ? error.message : "Failed to start product creation");
                    d({ type: "setCreating", creating: false });
                    return;
                }
                return;
            }
            try {
                const preflight = await scheduleDraftContextMeshAnalysis(
                    token,
                    persisted.draftId,
                    persisted.draftVersion
                );
                if (!isExactReadyDraftContextMesh(preflight, persisted.draftId, persisted.draftVersion)) {
                    preflightObservedPendingRef.current = false;
                    setCreatePreflightBlocked(true);
                    draftMesh.retry();
                    toast.info("Context analysis is still finishing. Create will be available when it is ready.");
                    d({ type: "setCreating", creating: false });
                    return;
                }
            } catch (error) {
                preflightObservedPendingRef.current = false;
                setCreatePreflightBlocked(true);
                draftMesh.retry();
                toast.error(error instanceof Error ? error.message : "Context analysis is not ready yet.");
                d({ type: "setCreating", creating: false });
                return;
            }
            try {
                const response = await fetch(`${AGENT_URL}/api/product-meshes`, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json"
                    },
                    credentials: "include",
                    body: JSON.stringify(buildProductMeshSubmission(state))
                });
                const result = await response.json().catch(() => ({}));
                if (!response.ok) {
                    throw new Error(productCreationErrorMessage(result, response.status));
                }
                if (result.status === "push_failed" || result.project_id == null) {
                    const warning = Array.isArray(result.warnings) ? result.warnings.join(" ") : "Product creation did not complete.";
                    throw new Error(warning);
                }

                const pid = Number(result.project_id);
                if (!Number.isSafeInteger(pid) || pid <= 0) {
                    throw new Error("Product creation returned an invalid product identifier.");
                }
                // Promote the exact, preflighted Discover context before any
                // downstream attachment/build work can consume the new Product.
                // A failed completion leaves the draft active and must stop the
                // build pipeline.
                try {
                    await completeProductCreationDraft(token, persisted.draftId, pid);
                } catch (error) {
                    throw new Error(
                        error instanceof Error
                            ? `Product created, but Context Mesh finalization failed: ${error.message}`
                            : "Product created, but Context Mesh finalization failed."
                    );
                }
                const stagedSources = buildStagedAttachSources();
                try {
                    await startProductBuild(pid, stagedSources, token, buildProductCreationContext(state));
                } catch {
                    toast.error("Product created, but processing needs attention — use the Product card to retry.");
                }
                toast.success(`${state.fields.name.trim()} created`);
                if (draftRef) draftRef.current = null;
                // Product Hub owns current-product selection and list refresh.
                // Keeping those operations out of this awaited creation path
                // lets navigation happen as soon as build registration settles.
                onCreated(pid);
            } catch (err) {
                toast.error(typeof err === "string" ? err : err instanceof Error ? err.message : "Failed to create product");
                d({ type: "setCreating", creating: false });
            }
        } finally {
            isSubmittingRef.current = false;
        }
    };

    const handleSaveDraft = async () => {
        if (!dirty) return;
        try {
            await persistDraft(state);
            toast.success(`${isStandaloneContextMesh ? "Context Mesh" : "Product"} draft saved`);
            setPendingConfirm("saveDraft");
        } catch (error) {
            toast.error(error instanceof Error ? error.message : `Failed to save ${isStandaloneContextMesh ? "Context Mesh" : "product"} draft`);
        }
    };

    const performDiscardDraft = async (): Promise<boolean> => {
        if (!token) {
            toast.error("Your session hasn't finished loading — try again in a moment.");
            return false;
        }
        // A Cancel click can race the debounced autosave. Let any request that
        // already entered the queue publish its draft id before deleting it.
        await saveQueueRef.current.catch(() => undefined);
        try {
            if (draftIdRef.current != null) {
                await discardProductCreationDraft(token, draftIdRef.current, { cleanupStaged: true });
                draftIdRef.current = null;
            }
            savedRevisionRef.current = null;
            setPersistedDraftIdentity({ draftId: null, version: null });
            d({ type: "reset" });
            setSavedFingerprint(JSON.stringify(buildPersistedNewProductDraftState(initialNewProductState, draftOffering, draftPurpose)));
            setLastSavedAt(null);
            setSaveStatus("idle");
            toast.success(`${isStandaloneContextMesh ? "Context Mesh" : "Product"} draft discarded`);
            return true;
        } catch (error) {
            toast.error(error instanceof Error ? error.message : `Failed to discard ${isStandaloneContextMesh ? "Context Mesh" : "product"} draft`);
            return false;
        }
    };

    const requestExit = (exitAction: () => void) => {
        // Refresh mode never autosaves/stages anything (behavior 6), so there
        // is nothing queued to lose silently on Cancel — but real in-progress
        // edits (dirty vs. the loaded sourceRef) can still be discarded
        // without warning, unlike create mode. Reuse the same confirm-dialog
        // guard create mode uses for unsaved work; exit immediately when
        // there's nothing to lose.
        if (isContextMeshRefresh) {
            if (contextRefreshDirtyVsSource) {
                pendingExitActionRef.current = exitAction;
                setPendingConfirm("cancel");
            } else {
                void performDiscardDraft().then(discarded => { if (discarded) exitAction(); });
            }
            return;
        }
        if (isProductRefresh) {
            if (refreshDirtyVsSource) {
                pendingExitActionRef.current = exitAction;
                setPendingConfirm("cancel");
            } else if (refreshPreviewDraftIdRef.current == null) {
                exitAction();
            } else {
                void discardRefreshPreview()
                    .catch(error => console.error("Failed to discard Context Mesh preview", error))
                    .finally(exitAction);
            }
            return;
        }
        // Standalone Context Mesh drafts are durable Hub items. Leaving never
        // deletes the draft or its staged sources; the confirmation merely
        // gives the current edit a chance to finish saving before navigation.
        if (isStandaloneContextMesh) {
            if (dirty) {
                pendingExitActionRef.current = exitAction;
                setPendingConfirm("cancel");
            } else {
                exitAction();
            }
            return;
        }
        // Gate on form content, not hasUnsavedChanges: autosave (750ms debounce)
        // clears "unsaved" almost immediately, which would let Cancel exit a
        // non-empty draft without ever warning.
        if (dirty) {
            pendingExitActionRef.current = exitAction;
            setPendingConfirm("cancel");
        } else {
            exitAction();
        }
    };

    // Refresh mode (KAN-1464 Task 7): staged scan -> verdict -> commit flow.
    //
    // Staged-write invariant: nothing below writes to the product until the
    // user confirms a verdict dialog (handleRefreshConfirm*). handleRefreshScan
    // only calls runRefreshScan (a read-only WPP comparison job) and routes the
    // classified verdict to the matching dialog — it never calls a commit/write
    // function itself.
    const buildEditedCoreValues = (): RefreshCoreValues => {
        const source = sourceRef;
        return {
            name: state.fields.name,
            problem: state.fields.problem,
            solution: state.fields.solution,
            users: normList(state.users).join("\n"),
            team: normList(state.team).join("\n"),
            nfr: { ...state.nfr },
            // type/description have no UI in refresh mode — pass through
            // unchanged from the loaded source (UpdateProductModal.tsx's
            // formData.description is likewise the raw project_description
            // passed through untouched, never derived from problem/solution).
            type: source?.core.type ?? "",
            description: source?.core.description ?? ""
        };
    };

    const finishRefresh = async (outcome: { kind: "updated" } | { kind: "rebuilt"; newProjectId: number }) => {
        const completedProductId = outcome.kind === "rebuilt" ? outcome.newProjectId : refreshProductId;
        if (token && completedProductId != null) {
            try {
                // The refresh endpoint promotes the exact active preview's
                // Discover values before recalculating the saved Product. Do
                // not delete that preview concurrently with promotion.
                if (refreshProductId != null && refreshPreviewDraftIdRef.current != null) {
                    await requestProductContextMeshAnalysis(token, String(completedProductId), {
                        previewProductId: refreshProductId
                    });
                } else {
                    await requestProductContextMeshAnalysis(token, String(completedProductId));
                }
            } catch (error) {
                // The Product update is already committed. Preserve it and the
                // last valid score. Keep the staged preview so a later retry
                // can still promote the exact evaluated Discover values.
                console.error("Failed to refresh the final Context Mesh score", error);
                onRefreshComplete?.(outcome);
                return;
            }
        }
        try {
            await discardRefreshPreview();
        } catch (error) {
            console.error("Failed to discard completed Context Mesh preview", error);
        }
        onRefreshComplete?.(outcome);
    };

    // Keep the shared Files chip synchronized with the modal after each
    // successful add/remove, matching create-mode's immediate count update.
    const handleRefreshFilesUpdated = (files: RefreshSource["files"]) => {
        setSourceRef(prev => (prev ? { ...prev, files } : prev));
        d({ type: "setConnection", source: "files", connection: refreshFilesConnection(files) });
    };

    // File writes are immediate, but derived work products are marked once
    // per modal session rather than once per file.
    const handleRefreshFilesChanged = (files: RefreshSource["files"]) => {
        handleRefreshFilesUpdated(files);
        setRefreshFilesChanged(true);
        if (!token || refreshProductId == null) return;
        markFilesChanged(token, refreshProductId).catch(error => {
            console.error("Failed to mark work products for rebuild after file changes", error);
        });
    };

    const getRefreshLinkIdsToRemove = (): number[] =>
        (Object.entries(state.connections) as [SourceId, SourceConnection][])
            .filter(
                ([source, connection]) =>
                    source !== "files" && Boolean(connection.existingLinkIds?.length) && (!connection.connected || hasStagedSource(connection))
            )
            .flatMap(([, connection]) => connection.existingLinkIds ?? []);

    const applyRefreshIntegrationChanges = async (targetProjectId: number) => {
        if (!token || !refreshIntegrationDirtyVsSource) return;
        const attached = await attachStagedArtifacts(
            targetProjectId,
            buildStagedAttachSources(true),
            token,
            getRefreshLinkIdsToRemove()
        );
        if (attached.context_mesh_refresh?.scheduled === false) {
            toast.warning(
                `${attached.warning || "Context Mesh refresh needs attention."} Request ID: ${attached.context_mesh_refresh.request_id}`
            );
        }
        await markIntegrationsChanged(token, targetProjectId);
    };

    const cleanupRefreshStagedChanges = async () => {
        if (!token) return;
        const staged = collectStagedIds(state);
        if (!staged.processJobIds.length && !staged.repoIds.length && !staged.fileIds.length) return;
        await cleanupStagedArtifacts(staged, token);
    };

    const handleRefreshScan = async () => {
        if (!sourceRef || !token || userId == null || refreshProductId == null) {
            toast.error("Your session hasn't finished loading — try again in a moment.");
            return;
        }
        const edited = buildEditedCoreValues();
        setRefreshPhase("scanning");
        try {
            const outcome = await runRefreshScan({
                token,
                projectId: refreshProductId,
                userId,
                originalAnswers: sourceRef.originalAnswers,
                newAnswers: buildNewAnswers(sourceRef.originalAnswers, sourceRef.nfrQuestionIds, edited),
                originalDescription: sourceRef.originalDescription,
                onPhase: (phase, message) => setBuildProgress({ phase, message })
            });
            // Product name and team (Q25) aren't part of the scan payload
            // (buildNewAnswers only covers Q20/22/23/24), so a name-only or
            // team-only edit always scans as NO_CHANGES. If we showed
            // NoChangesDialog here, the edit would never get written and the
            // CTA would just re-enable — an infinite loop, and a regression
            // vs. the old modal (which wrote the name pre-scan under every
            // verdict). Detect that case and write the core update directly
            // instead of surfacing the dialog. Truly nothing changed still
            // falls through to NoChangesDialog. Team compares semantically
            // (normList vs sourceRef.team) rather than string-vs-core.team so
            // legacy comma-separated Q25 answers don't false-positive here.
            const teamEdited = JSON.stringify(normList(state.team)) !== JSON.stringify(normList(sourceRef.team));
            const nfrEdited = NFR_FIELDS.some(
                field => edited.nfr[field.key] !== (sourceRef.core.nfr?.[field.key] ?? "")
            );
            if (
                outcome.verdict === "NO_CHANGES" &&
                (edited.name !== sourceRef.core.name ||
                    teamEdited ||
                    nfrEdited ||
                    refreshFilesChanged ||
                    refreshIntegrationDirtyVsSource)
            ) {
                if (edited.name !== sourceRef.core.name || teamEdited || nfrEdited) {
                    await writeCoreUpdates({
                        token,
                        projectId: refreshProductId,
                        answerIds: sourceRef.answerIds,
                        original: sourceRef.core,
                        edited
                    });
                }
                await applyRefreshIntegrationChanges(refreshProductId);
                toast.success("Product updated");
                setRefreshPhase("idle");
                setScanResult(null);
                await finishRefresh({ kind: "updated" });
                return;
            }
            setScanResult(outcome.result);
            setRefreshPhase({ verdict: outcome.verdict });
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Quick compare scan failed");
            setRefreshPhase("idle");
        }
    };

    // Dialog cancel (any verdict): client-side only — close the dialog and
    // return to editing. Deliberately does NOT call any commit/write
    // function: staged mode wrote nothing pre-confirm, so there is nothing
    // to undo. This intentionally departs from the old modal's CANCEL_UPDATE
    // apply-call-on-cancel — that concept doesn't apply to a flow that never
    // wrote in the first place.
    const handleRefreshDialogCancel = () => {
        setRefreshPhase("idle");
        setScanResult(null);
    };

    const handleRefreshConfirmMinorEvolution = async () => {
        if (!sourceRef || !token || userId == null || refreshProductId == null) return;
        const edited = buildEditedCoreValues();
        setRefreshPhase("committing");
        try {
            await commitMinorEvolution({
                token,
                projectId: refreshProductId,
                userId,
                answerIds: sourceRef.answerIds,
                original: sourceRef.core,
                edited,
                comparisonResult: scanResult,
                originalAnswers: sourceRef.originalAnswers
            });
            await applyRefreshIntegrationChanges(refreshProductId);
            toast.success("Product updated");
            setRefreshPhase("idle");
            setScanResult(null);
            await finishRefresh({ kind: "updated" });
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Failed to update product");
            setRefreshPhase("idle");
        }
    };

    const handleRefreshConfirmRefinementOnly = async () => {
        if (!sourceRef || !token || userId == null || refreshProductId == null) return;
        const edited = buildEditedCoreValues();
        setRefreshPhase("committing");
        try {
            await commitRefinementOnly({
                token,
                projectId: refreshProductId,
                userId,
                answerIds: sourceRef.answerIds,
                original: sourceRef.core,
                edited,
                comparisonResult: scanResult,
                originalAnswers: sourceRef.originalAnswers
            });
            await applyRefreshIntegrationChanges(refreshProductId);
            toast.success("Product updated");
            setRefreshPhase("idle");
            setScanResult(null);
            await finishRefresh({ kind: "updated" });
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Failed to update product");
            setRefreshPhase("idle");
        }
    };

    const handleRefreshConfirmDrasticChange = async (newProductName: string) => {
        if (!sourceRef || !token || userId == null || refreshProductId == null) return;
        setRefreshPhase("buildingDrastic");
        setBuildProgress({ phase: "" });
        try {
            const start: DrasticStartResult = await startDrasticChange({
                token,
                projectId: refreshProductId,
                userId,
                newProductName,
                comparisonResult: scanResult,
                originalAnswers: sourceRef.originalAnswers
            });
            await runDrasticBuild({
                token,
                start,
                onProgress: (phase, message) => setBuildProgress({ phase, message })
            });
            await applyRefreshIntegrationChanges(start.newProjectId);
            try {
                await dispatch(setCurrentProject(start.newProjectId)).unwrap();
            } catch {
                // Non-fatal: the new product exists; the dashboard refresh will pick it up.
            }
            setRefreshPhase("idle");
            setScanResult(null);
            await finishRefresh({ kind: "rebuilt", newProjectId: start.newProjectId });
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Failed to build new product");
            setRefreshPhase("idle");
        }
    };

    const createActionLabel = isContextMeshRefresh
        ? "Scan & apply changes"
        : isStandaloneContextMesh
        ? "Create Context Mesh"
        : state.kind !== "existing" ? "Create product" : state.scope === "multi" ? "Create features" : "Create feature";
    const createButton = (
        <NPButton
            tone="primary"
            disabled={!createIsReady || (isContextMeshRefresh && !contextRefreshDirtyVsSource)}
            onClick={handleCreate}
            style={{
                height: 38,
                gap: 8,
                padding: "0 16px",
                borderRadius: 9,
                fontSize: 13.5,
                fontWeight: 600,
                boxShadow: "0 2px 8px rgba(16, 54, 153, 0.3)"
            }}
            uiBridge={{
                id: isContextMeshRefresh ? "btn.refresh-context-mesh" : isStandaloneContextMesh ? "btn.create-context-mesh" : "btn.create-product",
                label: isContextMeshRefresh ? "Scan & apply changes" : isStandaloneContextMesh ? "Create Context Mesh" : "Create product",
                description: isStandaloneContextMesh
                    ? "Submit the form and create a standalone Context Mesh"
                    : "Submit the Add New Product form and create the product",
                group: "new-product"
            }}
        >
            {state.isCreating ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <NPIcon name="flag" size={14} color="#fff" />}
            {state.isCreating ? (isContextMeshRefresh ? "Applying…" : "Creating…") : createActionLabel}
        </NPButton>
    );

    const isScanning = refreshPhase === "scanning";
    const canScan =
        refreshDirtyVsSource &&
        state.fields.name.trim() !== "" &&
        state.fields.problem.trim() !== "" &&
        state.fields.solution.trim() !== "" &&
        hasRequiredTargetUser(state.users) &&
        Object.keys(nfrErrors).length === 0 &&
        !isScanning &&
        (!refreshScoreDirtyVsSource || refreshPreviewScoreIsCurrent);
    const scanButton = (
        <NPButton tone="primary" disabled={!canScan} onClick={() => void handleRefreshScan()}>
            {isScanning ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <NPIcon name="flag" size={14} color="#fff" />}
            {isScanning ? "Scanning…" : "Scan & apply changes"}
        </NPButton>
    );

    const revertEdits = async () => {
        if (isContextMeshRefresh && contextRefreshBaseline) {
            d({ type: "hydrate", state: contextRefreshBaseline });
            return;
        }
        if (!sourceRef) return;
        try {
            await cleanupRefreshStagedChanges();
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Failed to discard staged integration changes");
            return;
        }
        try {
            await discardRefreshPreview();
        } catch (error) {
            console.error("Failed to discard Context Mesh preview", error);
        }
        d({
            type: "hydrate",
            state: hydrateStateFromRefreshCore(sourceRef.core, sourceRef.users, sourceRef.team, {
                ...(sourceRef.connections ?? {}),
                files: refreshFilesConnection(sourceRef.files)
            })
        });
    };

    if (!draftLoaded || !refreshLoaded) {
        return (
            <div className="np-v6" style={{ display: "grid", placeItems: "center", height: "100%", background: "var(--bg)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--fg3)" }}>
                    <CircularProgress size={20} /> {isContextMeshRefresh ? "Loading Context Mesh…" : isRefresh ? "Loading product…" : isStandaloneContextMesh ? "Loading Context Mesh draft…" : "Loading product draft…"}
                </div>
            </div>
        );
    }

    if (isRefresh && refreshLoadError) {
        return (
            <div className="np-v6" style={{ display: "grid", placeItems: "center", height: "100%", background: "var(--bg)" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, color: "var(--fg3)" }}>
                    <span>Couldn't load this product for refresh.</span>
                    <NPButton tone="ghost" onClick={onBackToContextMesh ?? onCancel}>
                        {onBackToContextMesh ? "Back to Context Mesh" : "Back"}
                    </NPButton>
                </div>
            </div>
        );
    }

    const savedLabel =
        saveStatus === "saving"
            ? "Saving…"
            : saveStatus === "error"
              ? "Draft not saved"
              : lastSavedAt
                ? `Saved ${formatLocalDraftSaveTime(lastSavedAt)}`
                : "Not saved yet";

    return (
        <div className="np-v6" style={{ display: "flex", flexDirection: "column", height: "100%", background: "var(--bg)" }}>
            {/* Header */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    padding: "16px 28px",
                    borderBottom: "1px solid var(--divider)",
                    background: "var(--bg-paper)",
                    flex: "0 0 auto"
                }}
            >
                {isRefresh && onBackToContextMesh && (
                    <NavigationBackButton
                        label="Back to Context Mesh"
                        onClick={() => requestExit(onBackToContextMesh)}
                        description={isContextMeshRefresh
                            ? "Return to this Context Mesh score page"
                            : "Return to the product Context Mesh"}
                        bridgeId="btn.back-to-context-mesh"
                    />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                        <span style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-0.012em", whiteSpace: "nowrap" }}>
                            {isContextMeshRefresh ? "Refresh Context" : isRefresh ? "Refresh Product" : isStandaloneContextMesh ? "Create Context Mesh" : "Add New Product"}
                        </span>
                        {isContextMeshRefresh && (
                            <>
                                <span aria-hidden="true" style={{ fontSize: 20, fontWeight: 300, color: "var(--fg4)" }}>|</span>
                                <span title={refreshContextTitle ?? state.fields.name} style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", fontSize: 14, fontWeight: 500, color: "var(--fg1)", whiteSpace: "nowrap" }}>
                                    {refreshContextTitle ?? state.fields.name}
                                </span>
                            </>
                        )}
                    </div>
                    <div style={{ fontSize: 13, color: "var(--fg3)", marginTop: 3 }}>
                        {isContextMeshRefresh ? (
                            <>Editing "{refreshContextTitle ?? state.fields.name}" — changes are staged until you apply them.</>
                        ) : isRefresh ? (
                            <>Editing "{sourceRef?.core.name}" — changes are staged until you confirm the scan results.</>
                        ) : isStandaloneContextMesh ? (
                            <>Complete the required fields to create and analyze a standalone Context Mesh. No product, features, user stories, or work products will be generated.</>
                        ) : (
                            <>Complete the required fields. Everything else is us working for you {NP_DASH} confirm what's right, fix what isn't.</>
                        )}
                    </div>
                </div>
            </div>

            {isRefresh && <RefreshBanner contextMesh={isContextMeshRefresh} />}

            <ProgressRail steps={steps} onStepClick={scrollTo} />

            {/* Scrollable form region */}
            <div style={{ flex: 1, minHeight: 0, overflow: "hidden auto", padding: "26px 0" }}>
                <div style={{ margin: "0 auto", padding: "0 20px" }}>
                    {/* Hero row: form card + mesh card, inspector slides over */}
                    <div
                        className="np-rise"
                        style={{ display: "flex", gap: 22, alignItems: "stretch", marginBottom: 20, position: "relative" }}
                    >
                        <MeshInspector
                            open={state.meshOpen}
                            onClose={() => d({ type: "toggleMesh" })}
                            analysis={meshAnalysis}
                            updating={meshUpdating}
                        />
                        <div
                            ref={setRef("kind")}
                            style={{
                                flex: 1,
                                minWidth: 0,
                                background: "var(--bg-paper)",
                                border: "1.5px solid rgba(16, 54, 153,0.4)",
                                borderRadius: 18,
                                boxShadow: "var(--shadow-2)",
                                padding: "22px 26px"
                            }}
                        >
                            {isRefresh ? (
                                <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
                                    <span
                                        aria-label={isContextMeshRefresh ? "Context Mesh name" : "Product name"}
                                        style={{
                                            fontSize: 11.5,
                                            fontWeight: 700,
                                            padding: "5px 14px",
                                            borderRadius: 999,
                                            background: "var(--np-brand)",
                                            color: "#fff"
                                        }}
                                    >
                                        {isContextMeshRefresh
                                            ? refreshContextTitle ?? state.fields.name
                                            : sourceRef?.core.name}
                                    </span>
                                </div>
                            ) : (
                                <KindPills
                                    kind={state.kind}
                                    scope={state.scope}
                                    locked={locked}
                                    creationExperience={creationExperience}
                                    onKind={k => d({ type: "setKind", kind: k })}
                                    onScope={sc => d({ type: "setScope", scope: sc })}
                                    onCancel={() => requestExit(onCancel)}
                                />
                            )}
                            <div style={{ display: "flex", flexDirection: "column", gap: 13, ...lockStyle }}>
                                <div ref={setRef("name")}>
                                    <ExpandingField
                                        label={
                                            state.kind === "new"
                                                ? "Product Name"
                                                : state.scope === "multi"
                                                  ? "Feature Initiative Name"
                                                  : "Feature Name"
                                        }
                                        value={state.fields.name}
                                        onChange={v => d({ type: "setField", field: "name", value: v })}
                                        onClear={() => d({ type: "clearField", field: "name" })}
                                        rows={1}
                                        required
                                    />
                                </div>
                                {!isProductRefresh && state.kind === "existing" && (
                                    <section
                                        ref={setRef("existing")}
                                        style={{ borderTop: "1px solid var(--divider)", paddingTop: 14, marginTop: 3 }}
                                    >
                                        <button
                                            type="button"
                                            aria-expanded={existingContextOpen}
                                            aria-controls="existing-product-context-panel"
                                            onClick={() => setExistingContextOpen(open => !open)}
                                            style={{
                                                width: "100%",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 9,
                                                padding: 0,
                                                border: 0,
                                                background: "transparent",
                                                color: "inherit",
                                                cursor: "pointer",
                                                textAlign: "left",
                                                fontFamily: "inherit"
                                            }}
                                        >
                                            <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--fg2)" }}>Existing Product Context</span>
                                            <RequiredBadge />
                                            <span
                                                aria-hidden
                                                style={{
                                                    marginLeft: "auto",
                                                    color: "var(--fg4)",
                                                    fontSize: 17,
                                                    lineHeight: 1,
                                                    transform: existingContextOpen ? "rotate(180deg)" : "none",
                                                    transition: "transform .15s ease"
                                                }}
                                            >
                                                ▾
                                            </span>
                                        </button>
                                        <div style={{ marginTop: 5, fontSize: 11.5, color: "var(--fg3)" }}>
                                            {isStandaloneContextMesh
                                                ? "Describe the product as it exists today so the mesh can analyze the proposed feature in context."
                                                : "Describe the product as it exists today so the requested feature is generated in the right product context."}
                                        </div>
                                        {answerLengthErrors.existingProductContext && (
                                            <div role="alert" style={{ marginTop: 6, color: "var(--error, #b42318)", fontSize: 11.5 }}>
                                                {answerLengthErrors.existingProductContext}
                                            </div>
                                        )}
                                        {existingContextOpen && (
                                            <div
                                                id="existing-product-context-panel"
                                                style={{ display: "grid", gridTemplateColumns: "1fr", gap: 13, marginTop: 16 }}
                                            >
                                                <ExpandingField
                                                    label="Existing Product Name"
                                                    value={state.existingProduct.name}
                                                    onChange={value => d({ type: "setExistingProductField", field: "name", value })}
                                                    onClear={() => d({ type: "clearExistingProductField", field: "name" })}
                                                    rows={1}
                                                    required
                                                    error={answerLengthErrors.existingProductName}
                                                />
                                                <ExpandingField
                                                    label="Existing Product Purpose"
                                                    value={state.existingProduct.purpose}
                                                    onChange={value => d({ type: "setExistingProductField", field: "purpose", value })}
                                                    onClear={() => d({ type: "clearExistingProductField", field: "purpose" })}
                                                    rows={2}
                                                    required
                                                />
                                                <ExpandingField
                                                    label="Relevant Existing Capabilities"
                                                    value={state.existingProduct.capabilities}
                                                    onChange={value => d({ type: "setExistingProductField", field: "capabilities", value })}
                                                    onClear={() => d({ type: "clearExistingProductField", field: "capabilities" })}
                                                    rows={2}
                                                    required
                                                />
                                                <ListEditorCard
                                                    icon="users"
                                                    title="Existing Product Users"
                                                    items={state.existingProduct.users}
                                                    addLabel="Add existing product user"
                                                    placeholder="e.g., Account administrators"
                                                    badge={<RequiredBadge />}
                                                    onChangeItem={(index, value) => d({ type: "setExistingProductUser", index, value })}
                                                    onRemoveItem={index => d({ type: "removeExistingProductUser", index })}
                                                    onAddItem={() => d({ type: "addExistingProductUser" })}
                                                    onClear={() => d({ type: "clearExistingProductUsers" })}
                                                />
                                            </div>
                                        )}
                                    </section>
                                )}
                                <section
                                    ref={setRef("feature")}
                                    style={!isRefresh ? { borderTop: "1px solid var(--divider)", paddingTop: 14, marginTop: 3 } : undefined}
                                >
                                    {!isRefresh && (
                                        <>
                                            <button
                                                type="button"
                                                aria-expanded={featureContextOpen}
                                                aria-controls="feature-context-panel"
                                                onClick={() => setFeatureContextOpen(open => !open)}
                                                style={{
                                                    width: "100%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 9,
                                                    padding: 0,
                                                    border: 0,
                                                    background: "transparent",
                                                    color: "inherit",
                                                    cursor: "pointer",
                                                    textAlign: "left",
                                                    fontFamily: "inherit"
                                                }}
                                            >
                                                <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--fg2)" }}>
                                                    {state.kind === "new"
                                                        ? "New Product Context"
                                                        : isStandaloneContextMesh
                                                          ? state.scope === "multi" ? "New Features Context" : "Single Feature Context"
                                                          : state.scope === "multi" ? "Features To Create" : "Feature To Create"}
                                                </span>
                                                <RequiredBadge />
                                                <span
                                                    aria-hidden
                                                    style={{
                                                        marginLeft: "auto",
                                                        color: "var(--fg4)",
                                                        fontSize: 17,
                                                        lineHeight: 1,
                                                        transform: featureContextOpen ? "rotate(180deg)" : "none",
                                                        transition: "transform .15s ease"
                                                    }}
                                                >
                                                    ▾
                                                </span>
                                            </button>
                                            <div style={{ marginTop: 5, fontSize: 11.5, color: "var(--fg3)" }}>
                                                {isStandaloneContextMesh
                                                    ? state.kind === "new"
                                                        ? "Describe the new product context this mesh should analyze."
                                                        : state.scope === "multi"
                                                          ? "Describe the shared feature initiative context this mesh should analyze."
                                                          : "Describe the single feature and how it should fit into the existing product."
                                                    : state.kind === "new"
                                                      ? "Describe the new product ProductStudio should create."
                                                      : state.scope === "multi"
                                                        ? "Describe the shared initiative; ProductStudio will determine cohesive features and create stories for each."
                                                        : "Describe the single feature and how it should fit into the existing product."}
                                            </div>
                                        </>
                                    )}
                                    {(isRefresh || featureContextOpen) && (
                                        <div
                                            id="feature-context-panel"
                                            style={{ display: "grid", gridTemplateColumns: "1fr", gap: 13, marginTop: isRefresh ? 0 : 16 }}
                                        >
                                            {!isRefresh && state.kind === "existing" && (
                                                <div
                                                    style={{
                                                        padding: "9px 11px",
                                                        borderRadius: 9,
                                                        background: "#f5f7fc",
                                                        border: "1px solid #e4e9f5",
                                                        color: "#526079",
                                                        fontSize: 11.5,
                                                        lineHeight: 1.45
                                                    }}
                                                >
                                                    <strong style={{ color: "#314776" }}>
                                                        {isStandaloneContextMesh ? "Context scope:" : "Generation context:"}
                                                    </strong>{" "}
                                                    Existing Product Context explains the surrounding application. The fields below define only the requested change.
                                                </div>
                                            )}
                                            <div>
                                                <ExpandingField
                                                    label={
                                                        state.kind === "new"
                                                            ? "Problem Product Solves"
                                                            : state.scope === "multi"
                                                              ? "Problem This Feature Initiative Solves"
                                                              : "Problem This Feature Solves"
                                                    }
                                                    value={state.fields.problem}
                                                    onChange={value => d({ type: "setField", field: "problem", value })}
                                                    onClear={() => d({ type: "clearField", field: "problem" })}
                                                    onBlur={value => evaluateQuality("problem", value)}
                                                    rows={2}
                                                    required
                                                    error={!isRefresh ? answerLengthErrors.problem : undefined}
                                                />
                                                <FieldQualityNote label="Problem" quality={quality.problem} />
                                            </div>
                                            <div>
                                                <ExpandingField
                                                    label={
                                                        state.kind === "new"
                                                            ? "What Solution Does The Product Offer"
                                                            : state.scope === "multi"
                                                              ? "What Capabilities Should Be Added"
                                                              : "What Should This Feature Do"
                                                    }
                                                    value={state.fields.solution}
                                                    onChange={value => {
                                                        cancelInference("solution");
                                                        d({ type: "setField", field: "solution", value });
                                                    }}
                                                    onClear={() => {
                                                        cancelInference("solution");
                                                        d({ type: "clearField", field: "solution" });
                                                    }}
                                                    onBlur={value => evaluateQuality("solution", value)}
                                                    placeholder="Describe the solution this product offers."
                                                    rows={2}
                                                    required
                                                    error={!isRefresh ? answerLengthErrors.solution : undefined}
                                                    badge={<InferButton onInfer={() => runInfer("solution")} disabled={!canInfer} busy={inferringFields.has("solution")} />}
                                                />
                                                <FieldQualityNote label="Solution" quality={quality.solution} />
                                            </div>
                                            {!isRefresh && (
                                                <div ref={setRef("users")} style={{ display: "flex", flexDirection: "column" }}>
                                                    <ListEditorCard
                                                        icon="users"
                                                        title={state.kind === "new" ? "Target Users" : "Users Affected By This Feature"}
                                                        items={state.users}
                                                        addLabel={state.kind === "new" ? "Add target user" : "Add affected user"}
                                                        placeholder={state.kind === "new" ? "e.g., Law firms" : "e.g., Mobile account holders"}
                                                        badge={
                                                            <>
                                                                <RequiredBadge />
                                                                <InferButton onInfer={() => runInfer("users")} disabled={!canInfer} busy={inferringFields.has("users")} />
                                                            </>
                                                        }
                                                        onChangeItem={(index, value) => d({ type: "setListItem", list: "users", index, value })}
                                                        onRemoveItem={index => updatePersonasAndEvaluate({ type: "removeListItem", list: "users", index })}
                                                        onAddItem={() => d({ type: "addListItem", list: "users" })}
                                                        onClear={() => updatePersonasAndEvaluate({ type: "clearList", list: "users" })}
                                                        onBlur={evaluatePersonasQuality}
                                                        error={answerLengthErrors.targetUsers}
                                                    />
                                                    <FieldQualityNote label="Target Users" quality={quality.personas} />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </section>
                                {!isRefresh && (
                                    <div ref={setRef("formFactor")}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 7 }}>
                                            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--fg2)", letterSpacing: "0.03em" }}>
                                                Primary Form Factor
                                            </span>
                                            <RequiredBadge />
                                        </div>
                                        <ToggleButtonGroup
                                            exclusive
                                            value={state.primaryFormFactor}
                                            // Required: the reducer ignores the null MUI sends when the
                                            // active pill is clicked again, so a choice is always set.
                                            onChange={(_, value: PrimaryFormFactor | null) => d({ type: "setPrimaryFormFactor", primaryFormFactor: value })}
                                            sx={{ gap: "10px", "& .MuiToggleButtonGroup-grouped": { margin: 0 } }}
                                        >
                                            {FORM_FACTOR_OPTIONS.map(option => (
                                                <ToggleButton key={option.value} disableRipple value={option.value} sx={FORM_FACTOR_PILL_SX}>
                                                    {option.label}
                                                </ToggleButton>
                                            ))}
                                        </ToggleButtonGroup>
                                    </div>
                                )}
                                <div
                                    ref={setRef("nfr")}
                                    data-testid="nfr-section"
                                    style={{
                                        marginTop: 3,
                                        paddingTop: 14,
                                        borderTop: "1px solid var(--divider)"
                                    }}
                                >
                                    <button
                                        type="button"
                                        aria-expanded={nfrOpen}
                                        aria-label={isRefresh ? (nfrOpen ? "Hide optional NFRs" : "Add or edit optional NFRs") : "Non-Functional Requirements"}
                                        onClick={() => setNfrOpen(open => !open)}
                                        style={{
                                            width: "100%",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 9,
                                            padding: 0,
                                            border: 0,
                                            background: "transparent",
                                            color: "inherit",
                                            cursor: "pointer",
                                            textAlign: "left",
                                            fontFamily: "inherit"
                                        }}
                                    >
                                        <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--fg2)" }}>
                                            {isRefresh ? (nfrOpen ? "Hide optional NFRs" : "Add or edit optional NFRs") : "Non-Functional Requirements"}
                                        </span>
                                        <OptionalBadge>
                                            {isStandaloneContextMesh ? "Optional — improves technical context" : "Optional — improves technical user stories"}
                                        </OptionalBadge>
                                        <span
                                            aria-hidden
                                            style={{
                                                marginLeft: "auto",
                                                color: "var(--fg4)",
                                                fontSize: 17,
                                                lineHeight: 1,
                                                transform: nfrOpen ? "rotate(180deg)" : "none",
                                                transition: "transform .15s ease"
                                            }}
                                        >
                                            ▾
                                        </span>
                                    </button>
                                    <div style={{ marginTop: 5, fontSize: 11.5, color: "var(--fg3)" }}>
                                        {isStandaloneContextMesh
                                            ? "Add technical constraints so they are included in Context Mesh analysis."
                                            : "Add technical constraints now to make them available to downstream story generation."}
                                    </div>
                                    {nfrOpen && (
                                        <div
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                                                gap: "15px 18px",
                                                marginTop: 16
                                            }}
                                        >
                                            {NFR_FIELDS.map(field => (
                                                <ExpandingField
                                                    key={field.key}
                                                    label={field.label}
                                                    value={state.nfr[field.key]}
                                                    onChange={value => {
                                                        cancelInference(field.inferField);
                                                        d({ type: "setNfrField", field: field.key, value });
                                                    }}
                                                    onClear={() => {
                                                        cancelInference(field.inferField);
                                                        d({ type: "clearNfrField", field: field.key });
                                                    }}
                                                    placeholder={field.placeholder}
                                                    rows={2}
                                                    maxLength={field.maxLength}
                                                    error={nfrErrors[field.key]}
                                                    badge={
                                                        <InferButton
                                                            onInfer={() => runInfer(field.inferField)}
                                                            disabled={!canInfer}
                                                            busy={inferringFields.has(field.inferField)}
                                                        />
                                                    }
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div style={{ flex: "0 0 240px", ...lockStyle }}>
                            <MeshCard
                                analysis={meshAnalysis}
                                status={meshStatus}
                                updating={meshUpdating}
                                stale={meshStale}
                                error={meshError}
                                open={state.meshOpen}
                                onToggle={() => d({ type: "toggleMesh" })}
                                onRetry={retryMesh}
                            />
                        </div>
                    </div>

                    {/* Drafted cards row */}
                    <div
                        className="np-rise"
                        style={{
                            display: "grid",
                            gridTemplateColumns: isRefresh ? "1fr 1fr" : "1fr",
                            gap: 12,
                            marginBottom: 22,
                            alignItems: "stretch",
                            ...lockStyle
                        }}
                    >
                        {isRefresh && (
                            <div ref={setRef("users")} style={{ display: "flex", flexDirection: "column" }}>
                                <ListEditorCard
                                    icon="users"
                                    title="Target Users"
                                    items={state.users}
                                    addLabel="Add target user"
                                    placeholder="e.g., Law firms"
                                    badge={
                                        <>
                                            <RequiredBadge />
                                            <InferButton onInfer={() => runInfer("users")} disabled={!canInfer} busy={inferringFields.has("users")} />
                                        </>
                                    }
                                    onChangeItem={(i, v) => d({ type: "setListItem", list: "users", index: i, value: v })}
                                    onRemoveItem={i => updatePersonasAndEvaluate({ type: "removeListItem", list: "users", index: i })}
                                    onAddItem={() => d({ type: "addListItem", list: "users" })}
                                    onClear={() => updatePersonasAndEvaluate({ type: "clearList", list: "users" })}
                                    onBlur={evaluatePersonasQuality}
                                />
                                <FieldQualityNote label="Target Users" quality={quality.personas} />
                            </div>
                        )}
                        <div ref={setRef("team")} style={{ display: "flex", flexDirection: "column" }}>
                            <ListEditorCard
                                icon="team"
                                title="Team Members"
                                items={state.team}
                                addLabel="Add team member"
                                placeholder="e.g., Jordan Lee — Product Manager"
                                // Team (Q25) is never part of the refresh scan payload; edits
                                // persist through writeCoreUpdates' direct-write path (the same
                                // one that handles renames), so the card is editable in both modes.
                                badge={<OptionalBadge>{"Optional — Highly Suggested"}</OptionalBadge>}
                                onChangeItem={(i, v) => d({ type: "setListItem", list: "team", index: i, value: v })}
                                onRemoveItem={i => d({ type: "removeListItem", list: "team", index: i })}
                                onAddItem={() => d({ type: "addListItem", list: "team" })}
                                onClear={() => d({ type: "clearList", list: "team" })}
                                error={!isRefresh ? answerLengthErrors.team : undefined}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Integrations — pinned above the footer */}
            {!isContextMeshRefresh && (
                <div ref={setRef("integrations")} style={{ ...lockStyle }}>
                    <IntegrationsBar connections={state.connections} onOpenSource={setOpenSource} blockedSources={trackerBlocked} />
                </div>
            )}

            {openSource && openSource !== "files" && token && userId != null && (
                <ConnectSourceModal
                    source={openSource as ConnectSourceId}
                    token={token}
                    userId={userId}
                    existing={state.connections[openSource]}
                    onComplete={connection => handleSourceComplete(openSource, connection)}
                    onDisconnect={() => {
                        handleSourceDisconnect(openSource);
                        setOpenSource(null);
                    }}
                    onClose={() => setOpenSource(null)}
                    onAborted={handleSourceAborted}
                    draftId={persistedDraftIdentity.draftId}
                    onRepositorySelectionStaged={isRefresh
                        ? undefined
                        : connection => handleRepositorySelectionStaged(openSource, connection)}
                />
            )}

            {openSource === "files" && token && isRefresh && refreshProductId != null && sourceRef && (
                <RefreshFilesModal
                    files={sourceRef.files}
                    token={token}
                    projectId={refreshProductId}
                    onFilesUpdated={handleRefreshFilesUpdated}
                    onChanged={handleRefreshFilesChanged}
                    onClose={() => setOpenSource(null)}
                />
            )}

            {openSource === "files" && token && !isRefresh && (
                <FilesModal
                    existing={state.connections.files}
                    token={token}
                    onFileAdded={handleFilesUploaded}
                    onFileRemoved={handleFileRemoved}
                    onClose={() => setOpenSource(null)}
                    intakeFiles={intakeFiles}
                    onIntakeConsumed={onIntakeConsumed}
                />
            )}

            {isProductRefresh && (
                <>
                    <ScanProgressOverlay open={refreshPhase === "scanning"} phase={buildProgress.phase} message={buildProgress.message} />
                    <NoChangesDialog open={typeof refreshPhase === "object" && refreshPhase.verdict === "NO_CHANGES"} onClose={handleRefreshDialogCancel} />
                    <RefinementOnlyDialog
                        open={typeof refreshPhase === "object" && refreshPhase.verdict === "REFINEMENT_ONLY"}
                        result={scanResult}
                        onConfirm={() => void handleRefreshConfirmRefinementOnly()}
                        onCancel={handleRefreshDialogCancel}
                    />
                    <MinorEvolutionDialog
                        open={typeof refreshPhase === "object" && refreshPhase.verdict === "MINOR_EVOLUTION"}
                        result={scanResult}
                        onConfirm={() => void handleRefreshConfirmMinorEvolution()}
                        onCancel={handleRefreshDialogCancel}
                    />
                    <DrasticChangeDialog
                        open={typeof refreshPhase === "object" && refreshPhase.verdict === "DRASTIC_CHANGE"}
                        onConfirm={name => void handleRefreshConfirmDrasticChange(name)}
                        onCancel={handleRefreshDialogCancel}
                    />
                    <BuildProgressOverlay open={refreshPhase === "buildingDrastic"} phase={buildProgress.phase} message={buildProgress.message} />
                </>
            )}

            {pendingConfirm === "startOver" && (
                <ConfirmDialog
                    open
                    title="Start over?"
                    message={`Discard this ${isStandaloneContextMesh ? "Context Mesh" : "product"} draft and start over? This cannot be undone.`}
                    confirmLabel="Start over"
                    cancelLabel="Keep editing"
                    destructive
                    onConfirm={() => {
                        setPendingConfirm(null);
                        void performDiscardDraft();
                    }}
                    onCancel={() => setPendingConfirm(null)}
                />
            )}

            {pendingConfirm === "saveDraft" && (
                <ConfirmDialog
                    open
                    title="Draft saved"
                    message="Do you wish to continue working on this draft?"
                    confirmLabel="Yes, keep working"
                    cancelLabel={`No, back to ${isStandaloneContextMesh ? "Context Mesh Hub" : "Product Hub"}`}
                    onConfirm={() => setPendingConfirm(null)}
                    onCancel={() => {
                        setPendingConfirm(null);
                        onCancel();
                    }}
                    onClose={() => setPendingConfirm(null)}
                />
            )}

            {pendingConfirm === "cancel" && (
                <ConfirmDialog
                    open
                    title={isContextMeshRefresh ? "Discard refresh edits?" : isStandaloneContextMesh ? "Leave this draft?" : "Discard unsaved changes?"}
                    message={isContextMeshRefresh
                        ? "Your staged refresh edits will be lost. The existing Context Mesh will remain unchanged."
                        : isStandaloneContextMesh
                        ? "Your Context Mesh draft and its attached context will be kept in Context Mesh Hub so you can continue later."
                        : "Any unsaved changes will be lost. Are you sure?"}
                    confirmLabel={isContextMeshRefresh ? "Discard and leave" : isStandaloneContextMesh ? "Save and leave" : "Yes, leave"}
                    cancelLabel="Keep working"
                    destructive
                    onConfirm={() => {
                        setPendingConfirm(null);
                        const exitAction = pendingExitActionRef.current ?? onCancel;
                        pendingExitActionRef.current = null;
                        if (isContextMeshRefresh) {
                            void performDiscardDraft().then(discarded => { if (discarded) exitAction(); });
                        } else if (isProductRefresh) {
                            void cleanupRefreshStagedChanges()
                                .catch(error => {
                                    console.error("Failed to clean up staged refresh artifacts", error);
                                    toast.warning("Some staged integration data could not be cleaned up.");
                                })
                                .then(() => discardRefreshPreview())
                                .catch(error => {
                                    console.error("Failed to discard Context Mesh preview", error);
                                })
                                .finally(exitAction);
                        } else {
                            if (isStandaloneContextMesh) {
                                void persistDraft(latestStateRef.current)
                                    .then(() => exitAction())
                                    .catch(error => {
                                        toast.error(error instanceof Error ? error.message : "Failed to save Context Mesh draft");
                                    });
                            } else {
                                exitAction();
                            }
                        }
                    }}
                    onCancel={() => {
                        pendingExitActionRef.current = null;
                        setPendingConfirm(null);
                    }}
                />
            )}

            {/* Footer */}
            <div style={{ ...lockStyle }}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        padding: "14px 26px",
                        borderTop: "1px solid var(--divider)",
                        background: "var(--bg-paper)",
                        flex: "0 0 auto"
                    }}
                >
                    {isContextMeshRefresh ? (
                        <span style={{ fontSize: 12.5, color: "var(--fg3)" }}>
                            Context changes apply after analysis. Files, External and Code Context are not changed.
                        </span>
                    ) : isRefresh ? (
                        <span style={{ fontSize: 12.5, color: "var(--fg3)" }}>
                            Text and integration changes apply after the scan; file changes apply immediately.
                        </span>
                    ) : (
                        <>
                            {hasAnswerLengthErrors ? (
                                <span role="alert" style={{ fontSize: 12.5, color: "var(--error, #b42318)" }}>
                                    Shorten the highlighted work-product answer before creating.
                                </span>
                            ) : (
                                <span style={{ fontSize: 12.5, color: "var(--fg3)" }}>
                                    {isStandaloneContextMesh
                                        ? <>Create the mesh now and enrich its context over time. No product output is generated.</>
                                        : <>Create now, enrich later {NP_DASH} the mesh keeps growing after the product exists.</>}
                                </span>
                            )}
                            <span style={{ fontSize: 12, color: saveStatus === "error" ? "#b42318" : "var(--fg3)" }}>{savedLabel}</span>
                        </>
                    )}
                    <span style={{ flex: 1 }} />
                    {isRefresh ? (
                        <NPButton tone="ghost" onClick={() => void revertEdits()} disabled={isContextMeshRefresh ? !contextRefreshDirtyVsSource : !refreshDirtyVsSource}>
                            Revert edits
                        </NPButton>
                    ) : (
                        <>
                            {dirty && (
                                <NPButton tone="ghost" onClick={() => setPendingConfirm("startOver")} disabled={saveStatus === "saving"}>
                                    Start over
                                </NPButton>
                            )}
                            <NPButton tone="ghost" onClick={handleSaveDraft} disabled={!dirty || saveStatus === "saving" || !hasUnsavedChanges}>
                                Save draft
                            </NPButton>
                        </>
                    )}
                    <NPButton tone="ghost" onClick={() => requestExit(onCancel)}>
                        Cancel
                    </NPButton>
                    {isContextMeshRefresh ? createButton : isRefresh ? scanButton : createButton}
                </div>
            </div>
        </div>
    );
});
